import type { AuthUser } from '../core/state/auth-store';
import { socket } from '../core/network/socket';
import { ApiError } from '../core/network/api';
import {
  listCharacters, deleteCharacter, getCharacterSlots, buyCharacterSlot,
  type CharacterRow, type CharacterSlots,
} from './net/character-api';
import { openCharacterCreationWizard } from './character-creation-wizard';
import { characterSlotsMarkup } from './ui/character-slots';
import { classDisplayName } from './ui/class-picker';
import { openTalentManager } from './talent-manager-modal';
import { ITEM_DEFS } from './config/item-defs';
import { CHARACTER_ROSTER } from './config/character-roster';
import { getCommissions, deliverCommission } from './net/commissions-api';
import type { CommissionBoardResponse } from './net/commissions-api';
import { commissionBoardMarkup, boardTileSub } from './ui/commissions';
import { statLines } from './ui/paperdoll';
import {
  getGearCatalog, getWornGear, equipGear, purchaseGear, sellGear,
} from './net/gear-api';
import type { GearCatalogRow, GearBonuses, WornGear } from './net/gear-api';
import { GEAR_SLOT_LABEL, type GearSlot } from './config/gear-defs';
import {
  itemDetailMarkup, curioDetail, consumableDetail, riteDetail, cosmeticDetail, armsDetail, detailBuyButton,
} from './ui/item-detail';
import type { ItemDetailView } from './ui/item-detail';
import { sellPrice, expandStashCost } from './sim/pricing';
import {
  getBalance, sellItem, expandStash, getWaxBundles, purchaseWaxBundle,
  type Balance, type WaxShelf,
} from './net/economy-api';
import { listStash, type StashItemRow } from './net/hub-api';
import {
  joinQueue, leaveQueue, acceptPartyInvite, declinePartyInvite, leaveParty,
  acceptReadyCheck, declineReadyCheck,
  MATCHMAKING_EVENTS, PARTY_EVENTS, HUB_EVENTS,
  type MatchFoundPayload, type SquadMode, type PartyMode, type PartyInviteReceivedPayload,
  type PartyMemberJoinedPayload, type PartyMemberLeftPayload, type ReadyCheckPayload, type ReadyCheckFailedPayload,
} from './net/hub-socket-client';
import { getInsuranceStatus, expandInsurance, buyInsuranceCharges, type InsuranceStatus } from './net/insurance-api';
import { getContractCatalog, getMyContracts, purchaseContract, selectContract,
  type MapOptionRow, type ContractRow } from './net/contracts-api';
import {
  mapTileMarkup, mapDetailMarkup, destinationLabel, contractErrorMessage, type MapCardState,
} from './ui/map-picker';
import { amountMarkup } from './ui/currency';
import { balanceStripMarkup, TOP_UP_TARGETS, type BalanceKind, type BalancePlate } from './ui/balance-plates';
import { rewardLineMarkup } from './ui/quest-complete-banner';
import { AudioEngine } from './audio/audio-engine';
import { MusicToggle } from './ui/music-toggle';
import { openSettings, closeSettings } from './ui/settings/settings-modal';
import { bindAudioToSettings } from './ui/settings/apply-settings';
import { settingsStore } from './ui/settings/settings-store';
import { MUSIC_TRACKS } from './config/audio-tuning';
import {
  modeGridMarkup, modeBlurbMarkup, teamFillNoteMarkup, energyLineMarkup,
  playCtaMarkup, contractSectionNote, modeUsesContracts,
  launchCaptionMarkup, energyPlateSuffix,
} from './ui/play-flow';
import {
  getEnergy, getEnergyBundles, purchaseEnergyBundle,
  type EnergyStatus, type EnergyBundleRow,
} from './net/energy-api';
import { getEmberOffers, createEmberDeposit, getEmberLobbyOffer, type EmberOffers } from './net/ember-api';
import { openEmberOfferPopup, shouldShowOffer, markOfferShown, type EmberOfferPopup } from './ui/ember-offer-popup';
import { getPaymentMethods, getBillingAddress, getDeposit } from '../core/payments/payments-api';
import { clearPendingOrder } from '../core/payments/deposit-flow';
import { mountPaymentMethodPicker, type PaymentMethodPickerHandle } from '../core/payments/widgets/payment-method-picker';
import { mountCurrencySelector, type CurrencySelectorHandle } from '../core/payments/widgets/currency-selector';
import type { DepositMethodId, PaymentMethodsResponse, BillingAddressView } from '../core/payments/types';
import {
  getGameModes, toSquadMode, gameModeOf, squadSizeOf,
  type GameModeRow, type GameModeId,
} from './net/game-modes-api';
import {
  INSURANCE_DESCRIPTION, insuranceSlotsMarkup, insuranceFactsMarkup, insuranceCoverMarkup,
  insuranceTileSub, insuranceExpandLabel,
} from './ui/insurance-panel';
import {
  SHOP_TABS, shopTabsMarkup, gridMarkup, gearShelfCardMarkup,
  armsShelfCardMarkup,
  cosmeticCardMarkup, cosmeticLabel, consumableCardMarkup, riteCardMarkup, energyBundleCardMarkup, shelfHeadingMarkup, emberStorefrontMarkup,
  emberPreviewText, clampCents, formatEur, waxBundleCardMarkup, waxCapLineMarkup, consumableEffectLine,
  type ShopTabId,
} from './ui/shop';
import {
  getConsumables, purchaseConsumable, type ConsumableDefRow, type SatchelChargeRow,
} from './net/consumables-api';
import {
  getRites, purchaseRite, selectRite, type RiteDefRow,
} from './net/rites-api';
import { riteEffectLine, riteConditionLine } from './ui/rite-copy';
import { esc } from './ui/item-icon';
import { t, tc, tp, N_, formatNumber } from './i18n/i18n';
import { serverText } from './config/served-text';
import { getLoadout, type LoadoutRow } from './net/loadout-api';
import { loadoutMarkup } from './ui/loadout-panel';
import { inventoryMarkup, type InventoryView } from './ui/inventory-window';
import { searchRingMarkup } from './ui/search-spinner';
import { readyCheckExpiryAt, readyCheckSecondsLeft } from './sim/ready-check-clock';
import { renderWithCountUp } from './ui/count-up';
import { bandModeTabsMarkup, bandModeCopyMarkup, bandArtUrl, floorGaugeMarkup, floorTapAction } from './ui/descent-band';
import { bindHoldToPlay } from './ui/hold-to-play';
import {
  getEquipmentCatalog, getEquipment, equipItem, purchaseArms, sellArms,
  type EquipmentCatalogRow, type EquippedArms, type EquipmentSlot,
} from './net/equipment-api';
import {
  getCosmeticCatalog, getMyCosmetics, purchaseCosmetic, sellCosmetic, equipCosmetic, unequipCosmetic,
  type CosmeticDefRow, type CosmeticItemRow,
} from './net/cosmetics-api';
import { getActiveQuests, claimQuestReward, type UserQuestRow } from './net/quests-api';
import { questBarMarkup, questRewardMarkup, questKindStyle } from './ui/quest-progress';
import { HubToast } from './ui/hub-toast';
import { TutorialRunner } from './tutorial/tutorial-runner';
import { getTutorialState, markTutorialStepSeen, skipTutorial, completeTutorial } from './net/tutorial-api';
import { mountCharacterStage } from './render/character-stage';
import { rosterEntry } from './config/character-roster';
import { iconMarkup } from './ui/icon';
import { mountHubDepth } from './ui/hub-depth';
import { mountHubAmbience } from './ui/hub-ambience';
import { createPanelPortal } from './ui/panel-portal';
import { createBandArt } from './ui/band-depth';
import { playHubIntro } from './ui/hub-intro';
import {
  getLeaderboard, getLeaderboardModes,
  type LeaderboardModeId, type LeaderboardModeRow, type LeaderboardSort,
} from './net/leaderboards-api';
import { boardMarkup, modeTabsMarkup, sortTabsMarkup, standingMarkup } from './ui/leaderboard';

/**
 * design/gdd/sconce-hub.md — the shared room between runs. Genuinely thin:
 * every action here is a direct call to Economy/Currency's REST endpoints
 * or Matchmaking's Socket.io events (Core Rule #2); this file only renders
 * and wires those calls, it owns no game logic of its own.
 *
 * `onQueueFound` fires once `matchmaking.found` arrives — the caller
 * (session-bootstrap.ts) owns the actual transition into a Dungeon Instance
 * match, keeping this screen decoupled from match-mounting entirely.
 */
/**
 * The settings gear, on the same 24-unit grid and 1.7 stroke as `ui/icon.ts`.
 * Local rather than in the shared set because nothing else needs it, and a
 * one-use icon in a shared module is a module that grows for no reason.
 */
function gearIcon(size = 18): string {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor"`
    + ' stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">'
    + '<circle cx="12" cy="12" r="3.1" />'
    + '<path d="M12 2.6v2.2M12 19.2v2.2M21.4 12h-2.2M4.8 12H2.6'
    + 'M18.6 5.4l-1.6 1.6M7 17l-1.6 1.6M18.6 18.6L17 17M7 7L5.4 5.4" />'
    + '</svg>';
}

export function mountSconceHub(
  root: HTMLElement,
  user: AuthUser,
  onLogout: () => void,
  onQueueFound: (reservation: MatchFoundPayload['reservation']) => void,
  /**
   * Run-summary amendment (2026-08-24) — start searching immediately on mount,
   * for the run-end screen's "Queue Again". The hub still renders in full
   * behind the search overlay, so a player who changes their mind can cancel
   * and is exactly where they would have been anyway.
   */
  autoQueue = false,
): () => void {
  root.innerHTML = `
    <div data-hub="scroller" class="min-h-screen bg-lobby overflow-y-auto flex flex-col gap-3 p-3 sm:gap-4 sm:p-5 pb-[9.5rem] sm:pb-5">

      <div id="hub-hall" class="hall" aria-hidden="true"></div>

      <div id="hub-searching-overlay" class="fixed inset-0 z-50 scrim flex items-center justify-center p-4" hidden>
        <div class="frame-raised w-full max-w-[380px] p-6 flex flex-col gap-4 text-center">
          <h2 class="font-heading text-gold text-lg tracking-wide">${esc(t('Searching for a Match'))}</h2>
          ${searchRingMarkup('hub-searching-elapsed')}
          <p id="hub-searching-note" class="text-[13px] text-text-dim"></p>
          <button id="hub-searching-cancel" class="btn btn-quiet">${esc(t('Cancel'))}</button>
        </div>
      </div>

      <div id="hub-ready-check-overlay" class="fixed inset-0 z-50 scrim flex items-center justify-center p-4" hidden>
        <div class="frame-raised relative w-full max-w-[420px] p-6 flex flex-col gap-4">
          <h2 class="font-heading text-gold text-xl tracking-wide text-center">${esc(t('Match Found'))}</h2>
          <!--
            Labelled (2026-09-08). User: "Match was searching 17 seconds, but
            in confirmation it showed 15 seconds."

            Both numbers were right - the search had run 17s, and a Ready
            Check lasts 15s - but this one arrived as a bare gold number in
            the same weight and place the search overlay's had just been
            counting *up* in, so it read as the same clock disagreeing with
            itself. Naming what it counts is the whole fix.
          -->
          <p class="eyebrow eyebrow-dim text-center">${esc(t('Accept within'))}</p>
          <p id="hub-ready-check-countdown" class="text-center text-4xl font-heading text-gold tabular-nums"></p>
          <div id="hub-ready-check-roster" class="text-[13px] text-text-dim text-center"></div>
          <div class="flex gap-3">
            <button id="hub-ready-decline" class="btn btn-danger flex-1">${esc(t('Decline'))}</button>
            <button id="hub-ready-accept" class="btn btn-gold flex-1">${esc(t('Accept'))}</button>
          </div>
        </div>
      </div>

      <!--
        The item detail sheet (2026-09-04). User: "For each item, I should be
        able to read its usage and characteristics. It should also be clear who
        the item is suitable for and who it is not suitable for."

        Above the panel overlay rather than inside a panel, because it opens
        from four different places — the Shop's three shelves, the Stash grid
        and the paperdoll's arms picker — and a sheet that lived inside one of
        them would have to be built again in the others. On a phone it comes up
        from the bottom, which is where a sheet opened by a thumb belongs.
      -->
      <div id="hub-item-detail-overlay" class="fixed inset-0 z-50 scrim flex items-end sm:items-center justify-center overflow-y-auto p-0 sm:p-6" hidden>
        <div id="hub-item-detail-card" tabindex="-1"
             class="frame-raised relative w-full sm:max-w-[560px] max-h-[92svh] overflow-y-auto outline-none">
          <div class="flex items-center justify-between gap-3 px-4 pt-4 pb-2 sm:px-5 sm:pt-5">
            <h2 id="hub-item-detail-title" class="font-heading text-gold text-[15px] tracking-wide">${esc(t('Item'))}</h2>
            <button id="hub-item-detail-close" class="btn btn-quiet min-w-11 px-0 justify-center" aria-label="${esc(t('Close'))}">${iconMarkup('close')}</button>
          </div>
          <div id="hub-item-detail-body" class="px-4 pb-5 sm:px-5 sm:pb-6"></div>
        </div>
      </div>

      <div id="hub-panel-overlay" class="fixed inset-0 z-40 scrim flex items-start justify-center overflow-y-auto p-3 sm:p-6" hidden>
        <div id="hub-panel-card" tabindex="-1" class="frame-raised relative w-full max-w-[720px] my-auto outline-none">
          <div class="panel-head flex items-center justify-between gap-3 px-5 pt-5 pb-3">
            <h2 id="hub-panel-title" class="font-heading text-gold text-lg tracking-wide"></h2>
            <button id="hub-panel-close" class="btn btn-quiet min-w-11 px-0 justify-center" aria-label="${esc(t('Close'))}" data-label="${esc(t('Hall'))}">${iconMarkup('close')}</button>
          </div>
          <div class="px-5 pb-6 flex flex-col gap-4">

            <section data-panel="characters" hidden>
              <div id="hub-character-error" class="mb-3 notice-error" hidden></div>
              <div id="hub-character-list" class="stagger flex flex-col gap-2 mb-3"></div>
              <div id="hub-character-slots" class="flex flex-wrap items-center gap-2"></div>
            </section>

            <section data-panel="quests" hidden>
              <div id="hub-quests-error" class="mb-3 notice-error" hidden></div>
              <div id="hub-quests-list" class="stagger flex flex-col gap-2.5"></div>
            </section>

            <section data-panel="play" hidden>
              <div id="hub-play-energy-panel" class="mb-3"></div>

              <p class="eyebrow mb-1.5">${esc(t('Contract'))}</p>
              <p id="hub-play-contract-note" class="text-[12px] text-text-dim mb-2"></p>
              <div id="hub-play-contracts-error" class="mb-2 notice-error" hidden></div>
              <div id="hub-play-contracts" class="mb-4"></div>

              <p class="eyebrow mb-1.5">${esc(t('Game mode'))}</p>
              <div id="hub-play-modes" class="mb-2"></div>
              <div id="hub-play-mode-blurb" class="mb-4"></div>

              <div id="hub-play-team" class="mb-4"></div>

              <p class="eyebrow mb-1.5">${esc(t('Rite'))}</p>
              <p id="hub-play-rite-note" class="text-[12px] text-text-dim mb-2"></p>
              <div id="hub-play-rites" class="flex flex-col gap-1.5 mb-4"></div>

              <div id="hub-play-error" class="mb-3 notice-error" hidden></div>
              <button id="hub-play-confirm" class="btn btn-gold w-full min-h-[56px] px-6 text-base"></button>
            </section>

            <section data-panel="shop" hidden>
              <div id="hub-shop-tabs" class="mb-3"></div>

              <div data-shop-body="satchel" hidden>
                <p id="hub-satchel-note" class="mb-3 text-[12px] text-text-dim"></p>
                <div id="hub-satchel-error" class="mb-3 notice-error" hidden></div>
                <div id="hub-satchel-list"></div>
                <div id="hub-rites-shelf" class="mt-5"></div>
                <div id="hub-rites-error" class="mb-3 notice-error" hidden></div>
                <div id="hub-rites-list"></div>
              </div>

              <!--
                The Gear shelf (2026-09-04, opened to every tier 2026-09-06).
                See design/gdd/equipment.md on why the upper rungs are priced
                the way they are rather than withheld.
              -->
              <div data-shop-body="gear" hidden>
                <p class="text-[12px] text-text-dim mb-3">${esc(t('All seven worn slots, at all three depths. Everything here also drops on the floor it is named for — the deeper rungs are priced so that going and taking one is the cheaper way, not the slower one.'))}</p>
                <div id="hub-gear-error" class="mb-3 notice-error" hidden></div>
                <div id="hub-gear-list"></div>
              </div>

              <div data-shop-body="arms" hidden>
                <p class="text-[12px] text-text-dim mb-3">${esc(t('Weapons and off-hands. Your class came with its own kit and everyone carries a Dagger; everything else is bought here, and everything here can be sold back. Nothing outhits the Dagger for free — heavier weapons cost more stamina a swing and are slower to recover.'))}</p>
                <div id="hub-arms-error" class="mb-3 notice-error" hidden></div>
                <div id="hub-arms-list"></div>
              </div>

              <div data-shop-body="cosmetics" hidden>
                <p class="text-[12px] text-text-dim mb-3">${esc(t('Purely visual — Ember only, no gameplay effect. An equipped skin shows on the stage.'))}</p>
                <div id="hub-cosmetics-error" class="mb-3 notice-error" hidden></div>
                <div id="hub-cosmetics-catalog"></div>
              </div>

              <div data-shop-body="energy" hidden>
                <p class="text-[12px] text-text-dim mb-3">${esc(t('Every game costs 1 energy. You get a free bar back every day — these top it up beyond that, and what you buy never expires.'))}</p>
                <div id="hub-energy-error" class="mb-3 notice-error" hidden></div>
                <div id="hub-energy-status" class="mb-3"></div>
                <div id="hub-energy-bundles"></div>
              </div>

              <div data-shop-body="ember" hidden>
                <p class="text-[12px] text-text-dim mb-3">${esc(t('Ember is the currency you buy. It pays for cosmetics and energy, and it exchanges into Wax.'))}</p>
                <div id="hub-ember-error" class="mb-3 notice-error" hidden></div>
                <div id="hub-ember-body"></div>
              </div>

              <div data-shop-body="wax" hidden>
                <p class="text-[12px] text-text-dim mb-3">${esc(t('Wax is what you spend on gear, contracts and stash space. It is earned by delving — these turn Ember into it, at a fixed rate and a daily limit.'))}</p>
                <div id="hub-wax-error" class="mb-3 notice-error" hidden></div>
                <div id="hub-wax-body"></div>
              </div>
            </section>

            <section data-panel="commissions" hidden>
              <p class="text-[12px] text-text-dim mb-3">${esc(t('Three people at the Sconce want specific things today, and each pays more than the vendor would. What you hand over comes out of your stash.'))}</p>
              <div id="hub-commissions-error" class="mb-3 notice-error" hidden></div>
              <div id="hub-commissions-body"></div>
            </section>

            <section data-panel="contracts" hidden>
              <p class="text-[12px] text-text-dim mb-3">${esc(t('Where your next run starts. Pick a floor to see its contracts.'))}</p>
              <div id="hub-contracts-error" class="mb-3 notice-error" hidden></div>
              <div id="hub-contracts-catalog"></div>
            </section>

            <!--
              The full inventory (2026-09-02). User: "about inventory I've
              mentioned like full inventory button and full inventory window
              where I can interact." See ui/inventory-window.ts.
            -->
            <section data-panel="inventory" hidden>
              <div id="hub-inventory-error" class="mb-3 notice-error" hidden></div>
              <div id="hub-inventory-body"></div>
            </section>

            <section data-panel="rankings" hidden>
              <div id="hub-rankings-modes" class="mb-2"></div>
              <div id="hub-rankings-sorts" class="mb-3"></div>
              <div id="hub-rankings-error" class="mb-3 notice-error" hidden></div>
              <div id="hub-rankings-standing" class="mb-3"></div>
              <div id="hub-rankings-body"></div>
            </section>

            <section data-panel="insurance" hidden>
              <p id="hub-insurance-status" class="text-[13px] text-text-dim leading-snug mb-3"></p>
              <div id="hub-insurance-error" class="mb-3 notice-error" hidden></div>
              <div id="hub-insurance-cover" class="mb-3"></div>
              <div id="hub-insurance-slots" class="mb-3"></div>
              <div id="hub-insurance-facts" class="mb-3"></div>
              <button id="hub-insurance-expand-btn" class="btn btn-stone"></button>
            </section>

          </div>
        </div>
      </div>

      <header class="flex items-center gap-2 sm:gap-3 flex-wrap">
        <div class="flex items-center gap-3 mr-auto">
          <img src="/assets/ui/logo-480.webp" alt="Tallow" width="480" height="256"
               class="h-11 sm:h-16 w-auto select-none" draggable="false" />
          <span class="eyebrow eyebrow-dim hidden sm:inline">${esc(t('The Sconce'))}</span>
        </div>
        <!--
          Wrapping, not a nowrap row: at 375px the four plates are ~520px
          wide, and the header's own wrap could not reach inside this div — so
          Slots was clipped and Energy was off-screen entirely. Energy is what
          gates the Play button, which made "why can I not play?" unanswerable
          on the device most players are on.
        -->
        <div id="hub-balance" class="flex flex-wrap gap-2 text-sm"></div>
        <div id="hub-music-toggle" class="flex"></div>
        <button id="hub-settings" type="button" title="${esc(t('Settings'))}" aria-label="${esc(t('Settings'))}"
          class="btn btn-stone min-w-11 px-0 justify-center">${gearIcon()}</button>
        <button id="hub-logout" class="btn btn-stone px-4 eyebrow eyebrow-dim">${esc(t('Sign out'))}</button>
      </header>

      <!--
        The Descent Board (2026-10-09).

        User chose "C" of three Claude Design alternatives for the Sconce, and
        asked again that everything be animated and as interactive as
        possible. The six equal tiles plus a Play cell became one band that
        answers "where am I going next" - mode tabs, what the mode is, the
        floor it starts on, the delver standing in an arched doorway, and a
        Play you hold - with the six stations docked underneath.

        Every id the hub, the tutorial and the stage hook into is kept; only
        where they sit changed. The band's look is in app.css under "The
        Descent Board"; its markup builders are ui/descent-band.ts. (No
        backticks in this comment: one would end the template literal.)
      -->
      <div class="flex-1 flex flex-col gap-3 sm:gap-4 min-h-0">
        <section class="band" aria-label="${esc(t('Your next run'))}">
          <span id="hub-band-art" class="band-art" aria-hidden="true"></span>
          <span class="band-shade" aria-hidden="true"></span>
          <div class="band-grid">

            <div class="band-run">
              <div id="hub-band-modes" role="tablist" aria-label="${esc(t('Mode'))}" class="band-tabs"></div>
              <div id="hub-band-copy" class="min-h-0"></div>
              <div data-hub="launch" class="band-launch">
                <div id="hub-party-invite-banner" class="notice-warn" hidden></div>
                <div id="hub-queue-error" class="notice-error" hidden></div>
                <p id="hub-play-caption" class="text-[13px] text-text-dim leading-snug"></p>
                <div class="flex items-stretch gap-2">
                  <button id="hub-play-btn" type="button" class="btn btn-gold hold-btn flex-1 min-h-[60px] px-6 text-lg">${esc(t('Play'))}</button>
                  <button id="hub-run-options" type="button" class="btn btn-stone shrink-0 px-4" title="${esc(t('Rite and energy for this run'))}">${esc(t('Options'))}</button>
                </div>
                <div class="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <div id="hub-presence" class="text-[12px] text-text-dim/80 flex flex-wrap gap-x-1.5 gap-y-0.5">${esc(t('Just you, for now.'))}</div>
                  <p id="hub-queue-status" class="text-[13px] text-text-dim"></p>
                </div>
                <div id="hub-party-section" class="flex items-center justify-between gap-3 pt-2 border-t border-border" hidden>
                  <p id="hub-party-status" class="text-[13px] text-text-dim"></p>
                  <button id="hub-party-leave-btn" class="btn btn-quiet shrink-0">${esc(t('Leave'))}</button>
                </div>
              </div>
            </div>

            <div class="band-delver">
              <!--
                The stage renders its own alcove out of the dungeon's models
                (render/stage-set.ts); the portal only gives it an arched
                doorway to stand in, and the fog and candle pool sit on top.
              -->
              <div class="band-portal">
                <div id="hub-stage" class="absolute inset-0"></div>
                <div class="absolute inset-0 alcove-light pointer-events-none"></div>
                <span class="band-fog" aria-hidden="true"></span>
                <div class="band-plate">
                  <p id="hub-hero-name" class="font-heading text-xl sm:text-2xl leading-tight">&mdash;</p>
                  <p id="hub-hero-sub" class="eyebrow"></p>
                  <p id="hub-hero-meta" class="text-text-dim text-[12px]"></p>
                  <button id="hub-create-delver-btn" type="button" class="btn btn-gold mt-2 px-5" hidden>${esc(t('Create a delver'))}</button>
                  <span id="hub-talents-wrap" class="relative inline-block mt-2">
                    <button id="hub-talents-btn" type="button" class="btn btn-stone-on px-4 eyebrow">
                      <img src="/assets/ui/talents/_point.webp" alt="" aria-hidden="true" class="w-4 h-4 rounded-sm" />
                      ${esc(t('Talents'))}
                    </button>
                    <span id="hub-talents-badge" aria-hidden="true"
                      class="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 grid place-items-center rounded-full bg-gold-bright text-bg text-[11px] font-bold tabular-nums animate-ember pointer-events-none" hidden></span>
                  </span>
                </div>
              </div>
              <div class="flex items-center justify-center gap-2 overflow-x-auto py-1">
                <div id="hub-roster-rail" class="flex gap-2 shrink-0"></div>
                <button data-tile="characters" class="btn btn-stone shrink-0 w-[52px] h-[52px] !rounded-full px-0 justify-center" title="${esc(t('Manage delvers'))}" aria-label="${esc(t('Manage delvers'))}">${iconMarkup('add', 20)}</button>
              </div>
            </div>

            <div class="band-side">
              <p class="eyebrow">${esc(t('Floor'))}</p>
              <div id="hub-band-floors"></div>
              <div class="band-kit">
                <div id="hub-loadout"></div>
                <button id="hub-inventory-btn" type="button" class="btn btn-stone mt-2.5 w-full px-4 eyebrow eyebrow-dim">${esc(t('Open Inventory'))}</button>
              </div>
            </div>

          </div>
        </section>

        <nav class="dock stagger" aria-label="${esc(t('The Sconce'))}">
          <button data-tile="quests" class="station">
            <span class="station-disc"><img src="/assets/ui/lobby/tile-quests.webp" alt="" draggable="false" /></span>
            <span class="station-name">${esc(t('Quests'))}</span>
            <span id="hub-tile-quests-sub" class="station-sub"></span>
          </button>
          <button data-tile="contracts" class="station">
            <span class="station-disc"><img src="/assets/ui/lobby/tile-contracts.webp" alt="" draggable="false" /></span>
            <span class="station-name">${esc(t('Contracts'))}</span>
            <span id="hub-tile-contracts-sub" class="station-sub"></span>
          </button>
          <button data-tile="insurance" class="station">
            <span class="station-disc"><img src="/assets/ui/lobby/tile-insurance.webp" alt="" draggable="false" /></span>
            <span class="station-name">${esc(t('Insurance'))}</span>
            <span id="hub-tile-insurance-sub" class="station-sub"></span>
          </button>
          <button data-tile="commissions" class="station">
            <span class="station-disc"><img src="/assets/ui/lobby/tile-commissions.webp" alt="" draggable="false" /></span>
            <span class="station-name">${esc(t('Commissions'))}</span>
            <span id="hub-tile-commissions-sub" class="station-sub"></span>
          </button>
          <button data-tile="shop" class="station">
            <span class="station-disc"><img src="/assets/ui/lobby/tile-vendor.webp" alt="" draggable="false" /></span>
            <span class="station-name">${esc(t('Shop'))}</span>
            <span id="hub-tile-shop-sub" class="station-sub">${esc(t('Satchel & skins'))}</span>
          </button>
          <button data-tile="rankings" class="station">
            <span class="station-disc"><img src="/assets/ui/lobby/tile-arena.webp" alt="" draggable="false" /></span>
            <span class="station-name">${esc(t('Standings'))}</span>
            <span id="hub-tile-rankings-sub" class="station-sub">${esc(t('Delve, Arena & Waves'))}</span>
          </button>
        </nav>
      </div>

    </div>
  `;

  const $ = <T extends HTMLElement>(id: string) => root.querySelector(`#${id}`) as T;
  const balanceEl = $<HTMLDivElement>('hub-balance');
  const presenceEl = $<HTMLDivElement>('hub-presence');
  const partyInviteBannerEl = $<HTMLDivElement>('hub-party-invite-banner');
  const partySectionEl = $<HTMLElement>('hub-party-section');
  const partyStatusEl = $<HTMLParagraphElement>('hub-party-status');
  const partyLeaveBtn = $<HTMLButtonElement>('hub-party-leave-btn');
  /** The hub's big button: opens the Play flow when idle, and becomes the leave-queue control once queued. */
  const queueBtn = $<HTMLButtonElement>('hub-play-btn');
  /** One line under the presence list: the mode, the format, and where it lands you. */
  const playCaptionEl = $<HTMLParagraphElement>('hub-play-caption');
  /** The Descent Board's band — see ui/descent-band.ts. */
  const bandModesEl = $<HTMLDivElement>('hub-band-modes');
  const bandCopyEl = $<HTMLDivElement>('hub-band-copy');
  const bandFloorsEl = $<HTMLDivElement>('hub-band-floors');
  const bandArtEl = $<HTMLSpanElement>('hub-band-art');
  const bandArt = createBandArt(bandArtEl);
  const runOptionsBtn = $<HTMLButtonElement>('hub-run-options');
  // Its own slot beside the cards. The panel's general error line sits below
  // the mode picker and the format pills, which on a phone is a screen and a
  // half away from the card that was pressed.
  const playContractsErrEl = $<HTMLDivElement>('hub-play-contracts-error');
  const loadoutEl = $<HTMLDivElement>('hub-loadout');
  const inventoryBtn = $<HTMLButtonElement>('hub-inventory-btn');
  const inventoryBodyEl = $<HTMLDivElement>('hub-inventory-body');
  const inventoryErrEl = $<HTMLDivElement>('hub-inventory-error');
  const panelCardEl = $<HTMLDivElement>('hub-panel-card');
  // Depth (2026-10-13): layered stations, the parallax hall, drifting embers and
  // the step-into-a-station panel transition. All paint; see ui/hub-depth.ts.
  const hubScrollerEl = root.querySelector<HTMLElement>('[data-hub="scroller"]') ?? root;
  const hubDepth = mountHubDepth(hubScrollerEl, root.querySelector<HTMLElement>('#hub-hall'));
  const hubAmbience = mountHubAmbience(hubScrollerEl);
  const panelPortal = createPanelPortal(root, panelCardEl);
  playHubIntro(hubScrollerEl);
  const playEnergyPanelEl = $<HTMLDivElement>('hub-play-energy-panel');
  const playContractsEl = $<HTMLDivElement>('hub-play-contracts');
  const playContractNoteEl = $<HTMLParagraphElement>('hub-play-contract-note');
  const playModesEl = $<HTMLDivElement>('hub-play-modes');
  const playModeBlurbEl = $<HTMLDivElement>('hub-play-mode-blurb');
  const playTeamEl = $<HTMLDivElement>('hub-play-team');
  const playConfirmBtn = $<HTMLButtonElement>('hub-play-confirm');
  const energyErrEl = $<HTMLDivElement>('hub-energy-error');
  const energyStatusEl = $<HTMLDivElement>('hub-energy-status');
  const energyBundlesEl = $<HTMLDivElement>('hub-energy-bundles');
  const emberErrEl = $<HTMLDivElement>('hub-ember-error');
  const emberBodyEl = $<HTMLDivElement>('hub-ember-body');
  const playErrEl = $<HTMLDivElement>('hub-play-error');
  const characterErrEl = $<HTMLDivElement>('hub-character-error');
  const characterListEl = $<HTMLDivElement>('hub-character-list');
  const characterSlotsEl = $<HTMLDivElement>('hub-character-slots');

  const searchingOverlayEl = $<HTMLDivElement>('hub-searching-overlay');
  const searchingElapsedEl = $<HTMLParagraphElement>('hub-searching-elapsed');
  const searchingNoteEl = $<HTMLParagraphElement>('hub-searching-note');
  const searchingCancelBtn = $<HTMLButtonElement>('hub-searching-cancel');
  const readyCheckOverlayEl = $<HTMLDivElement>('hub-ready-check-overlay');
  const readyCheckCountdownEl = $<HTMLParagraphElement>('hub-ready-check-countdown');
  const readyCheckRosterEl = $<HTMLDivElement>('hub-ready-check-roster');
  const readyAcceptBtn = $<HTMLButtonElement>('hub-ready-accept');
  const readyDeclineBtn = $<HTMLButtonElement>('hub-ready-decline');
  const queueErrEl = $<HTMLDivElement>('hub-queue-error');
  const queueStatusEl = $<HTMLParagraphElement>('hub-queue-status');
  const satchelListEl = $<HTMLDivElement>('hub-satchel-list');
  const satchelNoteEl = $<HTMLParagraphElement>('hub-satchel-note');
  const satchelErrEl = $<HTMLDivElement>('hub-satchel-error');
  const ritesShelfEl = $<HTMLDivElement>('hub-rites-shelf');
  const ritesListEl = $<HTMLDivElement>('hub-rites-list');
  const ritesErrEl = $<HTMLDivElement>('hub-rites-error');
  const playRitesEl = $<HTMLDivElement>('hub-play-rites');
  const playRiteNoteEl = $<HTMLParagraphElement>('hub-play-rite-note');
  const waxErrEl = $<HTMLDivElement>('hub-wax-error');
  const waxBodyEl = $<HTMLDivElement>('hub-wax-body');
  const insuranceExpandBtn = $<HTMLButtonElement>('hub-insurance-expand-btn');
  const insuranceErrEl = $<HTMLDivElement>('hub-insurance-error');
  const insuranceStatusEl = $<HTMLParagraphElement>('hub-insurance-status');
  const insuranceSlotsEl = $<HTMLDivElement>('hub-insurance-slots');
  const insuranceCoverEl = $<HTMLDivElement>('hub-insurance-cover');
  const insuranceFactsEl = $<HTMLDivElement>('hub-insurance-facts');
  const contractsErrEl = $<HTMLDivElement>('hub-contracts-error');
  const contractsCatalogEl = $<HTMLDivElement>('hub-contracts-catalog');
  const cosmeticsErrEl = $<HTMLDivElement>('hub-cosmetics-error');
  const cosmeticsCatalogEl = $<HTMLDivElement>('hub-cosmetics-catalog');
  const shopTabsEl = $<HTMLDivElement>('hub-shop-tabs');
  const questsErrEl = $<HTMLDivElement>('hub-quests-error');
  const questsListEl = $<HTMLDivElement>('hub-quests-list');
  const toasts = new HubToast(root);

  /**
   * The hub half of the tutorial (design/gdd/tutorial.md).
   *
   * Declared here, before every function that signals into it, and started
   * only once the account's progress has come back — so it is `null` for the
   * first moment of the screen's life and every call site uses `?.`. Starting
   * it optimistically and correcting later would mean a returning player who
   * finished the tour six weeks ago briefly sees "This is the room between
   * runs" again, which is the one thing this feature must never do.
   */
  let tutorial: TutorialRunner | null = null;
  let offerPopup: EmberOfferPopup | null = null;
  let unmounted = false;

  /**
   * Quest completions this browser has already told the player about
   * (2026-09-01).
   *
   * A quest finished on a run completes in Postgres while the player is in the
   * dungeon, so the hub only ever sees it as an already-COMPLETED row on its
   * next fetch. "Newly complete" therefore has to mean "not announced yet",
   * not "changed since the last poll" — otherwise coming back from a run, the
   * one moment it matters, would announce nothing at all.
   *
   * Kept in `localStorage` for the same reason, so a refresh does not
   * re-congratulate. Ids are cuids, so there is no cross-account collision
   * worth keying against a user; the set is pruned to what is still
   * outstanding on every render so it cannot grow without limit.
   */
  /**
   * What buying from each cosmetics shelf actually gets you.
   *
   * Written without a digit anywhere on purpose, the same rule
   * `shared/item-copy.ts` holds the catalogue copy to: the emote gap is
   * `EMOTE_COOLDOWN_MS` and the day it is retuned this sentence must not
   * become the second, wrong place it is written down.
   */
  /**
 * design/gdd/rites.md — the terms, stated on the shelf.
 *
 * The lesson `6c469f2` paid for: the satchel two rows up sells *charges* that
 * count down, and a player reading a shelf that stated no terms at all
 * generalised from the nearest one that did. A Rite is neither a charge nor a
 * permanent unlock — it is a single-use commitment that may pay nothing — and
 * that is worth saying before the purchase rather than after the run.
 *
 * No digit appears in it. The count is "one", which is structural and will not
 * be retuned; every other quantity lives on the card, read off the server.
 */
const RITE_SHELF_NOTE = N_('Chosen before you descend and burned when the run starts — one a run, whatever happens after. Each pays on one kind of run and nothing on the others, so read what it wants before you buy it. Nothing is refunded and nothing comes home unspent.');

const EMOTE_SHELF_NOTE = N_('Bought once with Ember and yours for good — no charges, nothing to run out, on every character you own. Play them as often as you like; there is a short pause between plays and that is the only limit. Sell one back and you can buy it again.');
  const SKIN_SHELF_NOTE = N_('Bought once with Ember and yours for good. Each belongs to one class, one worn at a time, and none of them changes a single number in a fight.');

  const ANNOUNCED_KEY = 'tallow.quests.announced';
  function readAnnounced(): Set<string> {
    try {
      const raw = localStorage.getItem(ANNOUNCED_KEY);
      return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      // Private windows and blocked site-data both throw on access. A player
      // whose browser refuses storage should still get the hub, just with the
      // occasional repeated toast.
      return new Set<string>();
    }
  }
  function writeAnnounced(ids: Set<string>): void {
    try { localStorage.setItem(ANNOUNCED_KEY, JSON.stringify([...ids])); } catch { /* see above */ }
  }

  /**
   * Which delver you had picked, remembered (2026-09-07).
   *
   * User: *"From time to time, the selected weapon on my character changes
   * automatically in the hub."*
   *
   * It was not the weapon that changed, it was the character. `selectedCharacterId`
   * lived only in this closure, so every fresh mount of the hub — a page
   * reload, and every single return from a run — fell through to
   * `characters[0]`, which is the *oldest* character on the account
   * (`orderBy: createdAt asc`). Anyone playing their second or third delver
   * came back to the first one's paperdoll, holding the first one's weapon and
   * wearing the first one's gear, with nothing on screen to say the selection
   * had moved.
   *
   * Same `localStorage` treatment, and the same swallowed failure, as the
   * announced-quests set above: a browser that refuses storage gets the old
   * behaviour rather than an error.
   */
  const SELECTED_CHARACTER_KEY = 'tallow.hub.selectedCharacter';
  function readSelectedCharacter(): string | null {
    try { return localStorage.getItem(SELECTED_CHARACTER_KEY); } catch { return null; }
  }
  function writeSelectedCharacter(id: string | null): void {
    try {
      if (id) localStorage.setItem(SELECTED_CHARACTER_KEY, id);
      else localStorage.removeItem(SELECTED_CHARACTER_KEY);
    } catch { /* see above */ }
  }
  const rosterRailEl = $<HTMLDivElement>('hub-roster-rail');
  const heroNameEl = $<HTMLParagraphElement>('hub-hero-name');
  const heroSubEl = $<HTMLParagraphElement>('hub-hero-sub');
  const heroMetaEl = $<HTMLParagraphElement>('hub-hero-meta');
  const talentsBtn = $<HTMLButtonElement>('hub-talents-btn');
  const talentsBadgeEl = $<HTMLSpanElement>('hub-talents-badge');
  const talentsWrapEl = $<HTMLSpanElement>('hub-talents-wrap');
  const createDelverBtn = $<HTMLButtonElement>('hub-create-delver-btn');
  const stageHostEl = $<HTMLDivElement>('hub-stage');
  const gearErrEl = $<HTMLDivElement>('hub-gear-error');
  const gearListEl = $<HTMLDivElement>('hub-gear-list');
  const armsErrEl = $<HTMLDivElement>('hub-arms-error');
  const armsListEl = $<HTMLDivElement>('hub-arms-list');
  const commissionsErrEl = $<HTMLDivElement>('hub-commissions-error');
  const commissionsBodyEl = $<HTMLDivElement>('hub-commissions-body');
  const panelOverlayEl = $<HTMLDivElement>('hub-panel-overlay');
  const panelTitleEl = $<HTMLHeadingElement>('hub-panel-title');
  const panelCloseBtn = $<HTMLButtonElement>('hub-panel-close');

  /**
   * A tile that hides a whole section still has to say something about it, or
   * the player has to open all six to find out where anything stands. Missing
   * elements are tolerated so a future tile can drop its sub-label without
   * this throwing.
   */
  const setTileSub = (name: string, text: string): void => {
    const el = root.querySelector<HTMLElement>(`#hub-tile-${name}-sub`);
    if (el) el.textContent = text;
  };

  let balance: Balance | null = null;
  let stash: StashItemRow[] = [];
  let insurance: InsuranceStatus | null = null;
  let contractCatalog: MapOptionRow[] = [];
  let myContracts: ContractRow[] = [];
  /** null = the free Cellars default. design/gdd/contracts.md Core Rule #6 (amended). */
  let selectedContractId: string | null = null;
  /** design/gdd/energy.md — null until the first fetch lands. */
  let energy: EnergyStatus | null = null;
  let energyBundles: EnergyBundleRow[] = [];
  /** design/gdd/economy-currency.md Core Rule #8 — null until the offers land. */
  let emberOffers: EmberOffers | null = null;
  /** The custom field's value, in cents. Survives a re-render of the tab. */
  let emberCustomCents = 0;
  /** Core deposit method/currency/billing state. */
  let paymentMethodsData: PaymentMethodsResponse | null = null;
  let billingView: BillingAddressView | null = null;
  let selectedMethod: DepositMethodId = 'card';
  let selectedCurrency = 'EUR';
  let methodPickerHandle: PaymentMethodPickerHandle | null = null;
  let currencySelectorHandle: CurrencySelectorHandle | null = null;
  /** The Wax shelf plus how much of today's exchange allowance is left. */
  let waxShelf: WaxShelf | null = null;
  /** design/gdd/character-creation.md Core Rule #2 — three free, more for Ember. */
  let characterSlots: CharacterSlots | null = null;
  let buyingSlot = false;
  let gameModes: GameModeRow[] = [];
  /**
   * The Play flow's axes; `squadMode` stays the single wire value everything
   * else reads.
   *
   * `selectedSquadSize` is no longer chosen — the team-format picker was
   * removed on 2026-09-07 and every queue this client opens is a solo entry
   * that matchmaking fills. It is still a variable rather than a constant
   * because `adoptSquadMode` can be handed a Duo or Trio by the server on a
   * reconnect into a queue opened before the change, and silently rendering
   * that as Solo would be a worse lie than the one just removed.
   */
  let selectedGameMode: GameModeId = 'DUNGEON';
  let selectedSquadSize = 1;
  /** design/gdd/commissions.md — today's board. Null until the first fetch lands. */
  let commissionBoard: CommissionBoardResponse | null = null;
  let cosmeticCatalog: CosmeticDefRow[] = [];
  let myCosmetics: CosmeticItemRow[] = [];
  let myQuests: UserQuestRow[] = [];
  let consumableCatalog: ConsumableDefRow[] = [];
  let consumableStock: Record<string, number> = {};
  let plannedSatchel: SatchelChargeRow[] = [];
  let riteCatalog: RiteDefRow[] = [];
  let riteStock: Record<string, number> = {};
  let selectedRiteDefId: string | null = null;
  /** The loadout strip's data, and the character it was fetched for. */
  let loadout: LoadoutRow | null = null;
  let loadoutLoading = false;
  /** Which delver the strip was last fetched for; see renderCharacters(). */
  let loadoutCharacterId: string | null = null;
  /** The stash row open in the inventory's detail pane. */
  let inventorySelectedId: string | null = null;
  /** design/gdd/equipment.md — the paperdoll's two live slots. */
  let equipmentCatalog: EquipmentCatalogRow[] = [];
  let arms: EquippedArms | null = null;
  let armsCharacterId: string | null = null;
  let pickerSlot: EquipmentSlot | null = null;
  /** equipment.md's worn-gear amendment — the four slots the ladder filled. */
  let gearCatalog: GearCatalogRow[] = [];
  let wornGear: WornGear | null = null;
  let gearCharacterId: string | null = null;
  let gearPickerSlot: GearSlot | null = null;
  /** cosmetics-store.md — the Inventory's skin picker. One column, three pickers, one open at a time. */
  let skinPickerOpen = false;
  const NO_GEAR_BONUSES: GearBonuses = {
    physicalReduction: 0, magicResistance: 0, poise: 0, moveSpeedDelta: 0,
    hpBonus: 0, staminaBonus: 0, carryKg: 0, weightKg: 0, dodgeChance: 0,
  };
  /** The class the current satchel preview was fetched for; see renderCharacters(). */
  let satchelClass: string | null = null;
  let characters: CharacterRow[] = [];
  let selectedCharacterId: string | null = null;
  let queueState: 'idle' | 'queued' | 'ready_check' | 'found' = 'idle';
  /** Whether the Play panel's own button would start a run right now — the hold on the hub's Play is only live while it would. */
  let launchReady = false;
  /** What the band last drew, so a repaint that changes nothing does not restart its entrance animations. */
  let bandCopyMode: string | null = null;
  let bandArtUrlShown = '';
  let activeReadyCheck: ReadyCheckPayload | null = null;
  /** When the active Ready Check runs out, on *this* clock — see `onReadyCheck`. */
  let readyCheckExpiresAt = 0;
  let readyCheckCountdownTimer: ReturnType<typeof setInterval> | null = null;
  // matchmaking.md's MAX_QUEUE_WAIT_MS (45s) means a real, silent server-side
  // wait is normal — without visible progress this reads as "broken", not
  // "searching" (the exact confusion this ticker exists to prevent).
  let queueSearchStartedAt: number | null = null;
  let queueSearchTimer: ReturnType<typeof setInterval> | null = null;
  let squadMode: SquadMode = 'SOLO';
  // design/gdd/matchmaking.md Core Rule #1a — a party the local player leads
  // or belongs to; null when un-partied. This client only ever needs to know
  // its own party, never anyone else's.
  let myParty: { id: string; mode: PartyMode; leaderId: string; memberIds: string[] } | null = null;
  let incomingInvite: PartyInviteReceivedPayload | null = null;
  // sconce-hub.md Edge Case — the wire protocol only ever announces joins/leaves
  // after this client connects (Core Rule #1's getRoomMembers() isn't exposed
  // to the frontend), so anyone already in the hub before this tab connected
  // is invisible until they leave. A known, accepted limitation, not a bug.
  const presentUsers = new Map<string, string>();

  function showError(el: HTMLDivElement, message: string): void {
    el.textContent = message;
    el.hidden = false;
  }
  function hideError(el: HTMLDivElement): void {
    el.hidden = true;
  }
  function errorMessage(err: unknown): string {
    return serverText(err instanceof ApiError ? err.message : (err as Error).message) || t('Something went wrong');
  }

  /**
   * The header plates.
   *
   * Every one of them is a button, since 2026-09-03: the plate is where a
   * player is looking at the moment they find out they cannot afford
   * something, so it is where the route to more of it belongs. Which route
   * that is per balance is `ui/balance-plates.ts`'s table; `goToTopUp` below
   * is the only thing here that knows how to walk it.
   *
   * Energy joined them on 2026-09-02, when the bottom band that used to carry
   * it was cut. It is a balance you spend, exactly like the other three, and
   * the header is where this screen already answers "what do I have".
   *
   * Each plate now renders on the data it has rather than on all of it: the
   * wallet and the energy status arrive from two independent requests, and an
   * all-or-nothing early return meant whichever landed first showed nothing
   * until the other caught up. (The stash meter shipped with exactly that bug
   * once — see `refreshBalance`.)
   */
  function renderBalance(): void {
    const plates: BalancePlate[] = [];
    if (balance) {
      plates.push(
        { kind: 'wax', value: String(balance.soft), label: t('Wax'), tone: 'text-gold' },
        { kind: 'ember', value: String(balance.premium), label: t('Ember'), tone: 'text-purple-light' },
        { kind: 'slots', value: `${balance.stashUsed}/${balance.stashCapacity}`, label: t('Slots'), tone: 'text-text' },
      );
    }
    if (energy) {
      // Red the moment a run is unaffordable, so the reason the Play button
      // refuses is visible before it is pressed.
      const tone = energy.energy >= energy.costPerGame ? 'text-text' : 'text-error';
      plates.push({ kind: 'energy', value: String(energy.energy), label: t('Energy'), tone, note: energyPlateSuffix(energy) });
    }
    // Counts from the old numbers to the new rather than snapping.
    renderWithCountUp(balanceEl, () => { balanceEl.innerHTML = balanceStripMarkup(plates); });
    // Re-bound on every render: the strip is rebuilt from scratch, so the
    // previous buttons (and their listeners) no longer exist.
    balanceEl.querySelectorAll<HTMLButtonElement>('button[data-top-up]').forEach((btn) => {
      btn.addEventListener('click', () => goToTopUp(btn.dataset.topUp as BalanceKind));
    });
  }

  /**
   * Who else is at the Sconce, as one line.
   *
   * **Was a row per person until 2026-09-07.** Each name got its own flex row
   * with an Invite button on the right, which was the right shape when this
   * lived in a full-width bar at the bottom of the hub. It is a grid cell now,
   * roughly 300px wide and sharing its height with a caption and a button, and
   * a stacked list there grows the cell every time somebody walks in — one
   * player's arrival would resize the tile.
   *
   * A comma-joined line wraps instead of stacking, so a busy Sconce costs a
   * line or two rather than a row each.
   *
   * The Invite button is gone with the rows. It was already unreachable: it is
   * gated on `squadSize(squadMode) > 1`, and every queue this client opens has
   * been solo since the team-format picker was removed. Rendering a control
   * that can never appear is how dead markup survives a refactor — the party
   * plumbing it called into is untouched and documented in matchmaking.md,
   * ready for the friend list that would want it back.
   */
  function renderPresence(): void {
    if (presentUsers.size === 0) {
      presenceEl.textContent = t('Just you, for now.');
      return;
    }
    presenceEl.innerHTML = [...presentUsers.entries()].map(([userId, username]) => {
      const alreadyPartied = myParty?.memberIds.includes(userId) ?? false;
      return `<span>${escapeHtml(username)}${alreadyPartied ? ` <span class="text-gold">${esc(t('(party)'))}</span>` : ''}</span>`;
    }).join('<span class="text-text-dim/50">·</span>');
  }

  /** design/gdd/pvp-arena.md — a top-level Dungeon/Arena toggle; the squad-size selector below it always reflects whichever activity is currently selected. */
  /**
   * The Play flow (2026-09-02) — contract, then mode, then team format.
   *
   * Replaces `renderActivitySelector` / `renderModeSelector`, which put two
   * of those three decisions permanently on the hub and left the third behind
   * a different tile. `squadMode` is still the single piece of state the
   * queue, the party code and the socket all read; this picker just writes it
   * from a (mode, size) pair instead of from one button per combination.
   */
  /**
   * The one state every contract card is drawn from, so the Play panel and the
   * Contracts tile cannot disagree about whether a purchase is possible.
   */
  function mapCardState(): MapCardState {
    return { selectedContractId, contracts: myContracts };
  }

  /**
   * Which floor each picker has open (2026-10-09). Two, because the Contracts
   * tile and the Play panel's first step are separate places a player can be
   * reading — opening the Vaults in one should not open it in the other.
   */
  const mapDetail: { contracts: string | null; play: string | null } = { contracts: null, play: null };

  /**
   * The floor picker: tiles at rest, one floor's full detail once a tile is
   * pressed (see `mapTileMarkup`). Buying and selecting only exist in the
   * detail, so a purchase repaint keeps the reader where they were.
   */
  function paintMapPicker(el: HTMLElement, which: 'contracts' | 'play', repaint: () => void): void {
    const open = mapDetail[which] ? contractCatalog.find((o) => o.stratum.id === mapDetail[which]) : undefined;
    el.innerHTML = open
      ? mapDetailMarkup(open, mapCardState())
      : `<div class="stagger grid grid-cols-1 sm:grid-cols-3 gap-3">${
        contractCatalog.map((option) => mapTileMarkup(option, mapCardState())).join('')}</div>`;

    el.querySelectorAll<HTMLButtonElement>('button[data-open-map]').forEach((btn) => {
      btn.addEventListener('click', () => {
        mapDetail[which] = btn.dataset.openMap!;
        repaint();
        el.querySelector<HTMLElement>('[data-close-map]')?.focus({ preventScroll: true });
        el.scrollIntoView({ block: 'nearest' });
      });
    });
    el.querySelectorAll<HTMLButtonElement>('button[data-close-map]').forEach((btn) => {
      btn.addEventListener('click', () => { mapDetail[which] = null; repaint(); });
    });
    el.querySelectorAll<HTMLButtonElement>('button[data-buy-contract]').forEach((btn) => {
      btn.addEventListener('click', () => onPurchaseContract(btn.dataset.buyContract!));
    });
    el.querySelectorAll<HTMLButtonElement>('button[data-select-contract]').forEach((btn) => {
      // Empty value is the Cellars' Plain Delve — selecting "no contract".
      btn.addEventListener('click', () => onSelectContract(btn.dataset.selectContract || null));
    });
  }

  function renderPlayPanel(): void {
    const mode = gameModes.find((m) => m.id === selectedGameMode);
    const locked = queueState !== 'idle';

    playEnergyPanelEl.innerHTML = energyLineMarkup(energy);

    // Step 1 — contract. Real for a Delve, explicitly inert elsewhere.
    playContractNoteEl.textContent = contractSectionNote(selectedGameMode);
    if (modeUsesContracts(selectedGameMode)) {
      playContractsEl.hidden = false;
      paintMapPicker(playContractsEl, 'play', renderPlayPanel);
    } else {
      playContractsEl.hidden = true;
    }

    // Step 2 — mode.
    playModesEl.innerHTML = modeGridMarkup(gameModes, selectedGameMode);
    playModesEl.querySelectorAll<HTMLButtonElement>('button[data-play-mode]').forEach((btn) => {
      if (locked) btn.disabled = true;
      btn.addEventListener('click', () => selectGameMode(btn.dataset.playMode as GameModeId));
    });
    playModeBlurbEl.innerHTML = modeBlurbMarkup(mode);

    // Step 3 — who you are going in with. Not a choice any more: there is no
    // friend list to form a party through, and a solo queue is already how
    // you are given one (see `teamFillNoteMarkup`).
    playTeamEl.innerHTML = teamFillNoteMarkup(mode);

    // The full check, not just the mode's: it also refuses with no delver,
    // and it is what tells the hub's Play whether a hold may start the run.
    renderPlayConfirm();
    renderLaunchCaption();
  }

  /**
   * The one line on the hub that says what pressing Play will do.
   *
   * Replaces the pair it used to take — a "Delve · Solo" summary on one side
   * of the old bottom band and a "Starting in The Cellars" caption on the
   * other — which between them answered the same question twice, in two
   * places, across a strip of empty plaque.
   */
  function renderLaunchCaption(): void {
    const mode = gameModes.find((m) => m.id === selectedGameMode);
    playCaptionEl.innerHTML = launchCaptionMarkup(
      mode,
      destinationLabel(contractCatalog, myContracts, selectedContractId),
    );
    renderBand();
  }

  /**
   * The Descent Board's band: mode tabs, the mode's copy, the floor gauge and
   * the art behind them. It moves the same state the Play panel moves, so it
   * is repainted from the same places (this runs inside the launch caption,
   * which both the panel and the contract ledger already repaint).
   */
  function renderBand(): void {
    const mode = gameModes.find((m) => m.id === selectedGameMode);
    const locked = queueState !== 'idle';
    const state = mapCardState();

    bandModesEl.innerHTML = bandModeTabsMarkup(gameModes, selectedGameMode, locked);
    bandModesEl.querySelectorAll<HTMLButtonElement>('button[data-band-mode]').forEach((btn) => {
      btn.addEventListener('click', () => selectGameMode(btn.dataset.bandMode as GameModeId));
    });

    let modeChanged = false;
    if (mode && bandCopyMode !== mode.id) {
      // Slide in from the side the new tab is on (2026-10-13).
      const from = gameModes.findIndex((m) => m.id === bandCopyMode);
      const to = gameModes.findIndex((m) => m.id === mode.id);
      bandCopyEl.style.setProperty('--swap-x', from < 0 || to < 0 ? '0' : to > from ? '1' : '-1');
      modeChanged = bandCopyMode !== null;
      bandCopyMode = mode.id;
      bandCopyEl.innerHTML = bandModeCopyMarkup(mode);
    }

    bandFloorsEl.innerHTML = floorGaugeMarkup(contractCatalog, state, selectedGameMode);
    bandFloorsEl.classList.toggle('band-swap', modeChanged);
    bandFloorsEl.querySelectorAll<HTMLButtonElement>('button[data-band-floor]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const option = contractCatalog.find((o) => o.stratum.id === btn.dataset.bandFloor);
        if (!option) return;
        // A floor can only be picked for a Delve; tapping one from another
        // mode is read as "I want to go there", so the mode comes along.
        if (selectedGameMode !== 'DUNGEON') selectGameMode('DUNGEON');
        const tap = floorTapAction(option, mapCardState());
        if (tap.kind === 'select') { void onSelectContract(tap.contractId); return; }
        mapDetail.contracts = option.stratum.id;
        setPanel('contracts');
        renderMaps();
      });
    });

    const art = bandArtUrl(mode, contractCatalog, state);
    if (art !== bandArtUrlShown) {
      bandArtUrlShown = art;
      // Re-key the push-in so a new floor arrives rather than snapping.
      bandArtEl.classList.remove('band-art-in');
      void bandArtEl.offsetWidth;
      bandArtEl.classList.add('band-art-in');
    }
    bandArt.update(selectedGameMode, art);
  }

  /** Pull the picker's two axes back out of a wire mode set somewhere else (a party invite, a reconnect). */
  function adoptSquadMode(wire: typeof squadMode): void {
    if (gameModes.length === 0) return;
    selectedGameMode = gameModeOf(gameModes, wire);
    selectedSquadSize = squadSizeOf(gameModes, wire);
  }

  function selectGameMode(id: GameModeId): void {
    if (id === selectedGameMode) return;
    // Not while a queue is open (2026-10-16). The tile used to switch freely
    // with a Delve queue still running, so the screen said Arena while the
    // server was finding a Delve — and the Delve is where the player landed.
    if (queueState !== 'idle') return;
    // Same "leave first" rule the old activity switch had: a party formed
    // under one mode's wire value cannot carry into another's.
    if (myParty) leaveParty();
    selectedGameMode = id;
    const mode = gameModes.find((m) => m.id === id);
    // Keep the format if the new mode offers it, otherwise fall back to solo.
    if (mode && !mode.sizes.includes(selectedSquadSize)) selectedSquadSize = mode.sizes[0] ?? 1;
    syncSquadMode();
  }

  /**
   * Writes the picker's two axes back onto the one wire value everything
   * else reads. A mode with no room type maps to null and simply leaves
   * `squadMode` where it was — the CTA is what refuses, not this.
   */
  function syncSquadMode(): void {
    const mode = gameModes.find((m) => m.id === selectedGameMode);
    const wire = toSquadMode(mode, selectedSquadSize);
    if (wire) squadMode = wire;
    renderPlayPanel();
    renderPresence();
    renderQueueUi();
  }

  function renderParty(): void {
    if (!myParty) {
      partySectionEl.hidden = true;
      return;
    }
    partySectionEl.hidden = false;
    const names = myParty.memberIds.map((id) => (id === user.id ? t('You') : presentUsers.get(id) ?? id));
    const isLeader = myParty.leaderId === user.id;
    const partyVars = {
      mode: myParty.mode.replace('ARENA_', ''),
      count: myParty.memberIds.length,
      size: squadSize(myParty.mode),
      names: names.join(', '),
    };
    partyStatusEl.textContent = isLeader
      ? t('{mode} — {count}/{size}: {names} (you lead)', partyVars)
      : t('{mode} — {count}/{size}: {names}', partyVars);
  }

  function renderIncomingInviteBanner(): void {
    if (!incomingInvite) {
      partyInviteBannerEl.hidden = true;
      return;
    }
    partyInviteBannerEl.hidden = false;
    partyInviteBannerEl.innerHTML = `
      <div class="flex items-center justify-between gap-2">
        <span>${escapeHtml(t('{name} invited you to {mode}', { name: incomingInvite.fromUsername, mode: incomingInvite.mode }))}</span>
        <div class="flex gap-2 shrink-0">
          <button id="hub-invite-accept" class="btn btn-gold">${esc(t('Accept'))}</button>
          <button id="hub-invite-decline" class="btn btn-quiet">${esc(t('Decline'))}</button>
        </div>
      </div>
    `;
    $('hub-invite-accept').addEventListener('click', () => {
      // myParty/squadMode are set by the party.member_joined snapshot that
      // follows (sent to every member including the one who just accepted).
      acceptPartyInvite(incomingInvite!.partyId);
      incomingInvite = null;
      renderIncomingInviteBanner();
    });
    $('hub-invite-decline').addEventListener('click', () => {
      declinePartyInvite(incomingInvite!.partyId);
      incomingInvite = null;
      renderIncomingInviteBanner();
    });
  }

  /**
   * Tile -> panel routing (RPG re-layout, 2026-08-25).
   *
   * The hub used to stack twelve equal-weight cards in a 720px column, which
   * is exactly why it read as a form rather than a lobby. Every destination is
   * still here; seven of them now sit behind a tile so the screen has one
   * subject (your delver) and one verb (Enter).
   *
   * The section bodies were MOVED, not rewritten — each `[data-panel]` block
   * holds the same element ids, and every listener below is the one that was
   * already wired to them. That is deliberate: re-laying out a screen with
   * this much live behaviour is only safe if the behaviour is not touched.
   */
  const PANEL_TITLES: Record<string, string> = {
    characters: N_('Your Delvers'),
    quests: N_('Quests'),
    play: N_('Choose your run'),
    shop: N_('The Shop'),
    contracts: N_('Contracts'),
    commissions: N_('The Commission Board'),
    insurance: N_('Insurance'),
    inventory: N_('Inventory'),
    rankings: N_('Standings'),
  };
  let openPanelName: string | null = null;

  function setPanel(name: string | null): void {
    // Reopening a picker starts at its tiles, not inside the floor last read.
    if (name === 'contracts' && mapDetail.contracts !== null) { mapDetail.contracts = null; renderMaps(); }
    if (name === 'play' && mapDetail.play !== null) { mapDetail.play = null; renderPlayPanel(); }
    const wasOpen = openPanelName !== null;
    if (name === null && openPanelName !== null) panelPortal.exit(openPanelName);
    openPanelName = name;
    hubDepth.setPanelOpen(name !== null);
    hubAmbience?.setPaused(name !== null);
    // The tour's hub steps end when the player opens the thing they are about,
    // not when they press Next. Routed through here rather than through each
    // tile's own listener so the balance plates' shortcuts count too.
    if (name === 'contracts' || name === 'insurance' || name === 'shop' || name === 'play') {
      tutorial?.signal(`panel:${name}` as const);
    }
    // design/gdd/leaderboards.md — loaded on open, for the reason the render
    // function's own note gives.
    if (name === 'rankings') void renderRankings();
    panelOverlayEl.hidden = name === null;
    // The inventory is a grid beside a paper doll; at 720px the two columns
    // stack on a laptop, which is the one width this screen has to work at.
    panelCardEl.classList.toggle('max-w-[960px]', name === 'inventory');
    panelCardEl.classList.toggle('max-w-[720px]', name !== 'inventory' && name !== 'shop');
    // The shop is a room of its own, not a card over the hall (2026-10-13).
    // User: *"let the shop tab be a full page, not a popup"*. Same section,
    // same listeners — only the frame around it changes.
    const asPage = name === 'shop';
    panelOverlayEl.classList.toggle('is-page', asPage);
    panelCardEl.classList.toggle('is-page', asPage);
    panelCloseBtn.setAttribute('aria-label', asPage ? t('Back to the hall') : t('Close'));
    if (asPage) panelOverlayEl.scrollTop = 0;
    root.querySelectorAll<HTMLElement>('[data-panel]').forEach((el) => {
      el.hidden = el.dataset.panel !== name;
    });
    if (name) {
      panelTitleEl.textContent = PANEL_TITLES[name] ? t(PANEL_TITLES[name]) : '';
      panelPortal.enter(name, wasOpen);
      panelCardEl.focus({ preventScroll: true });
    }
  }

  inventoryBtn.addEventListener('click', () => {
    hideError(inventoryErrEl);
    renderInventory();
    setPanel('inventory');
  });
  panelCloseBtn.addEventListener('click', () => setPanel(null));
  // Backdrop click closes, but only the backdrop itself — a click that
  // bubbled up from inside the panel must not dismiss what it landed on.
  panelOverlayEl.addEventListener('click', (e) => { if (e.target === panelOverlayEl) setPanel(null); });
  /**
   * The item detail sheet.
   *
   * User: "For each item, I should be able to read its usage and
   * characteristics. It should also be clear who the item is suitable for and
   * who it is not suitable for."
   *
   * ## One delegated listener, not a binding per render
   *
   * Every shelf in this file re-renders its grid and re-binds its buttons, and
   * that is fine for a handful of buy buttons — but the detail trigger is on
   * *every tile on every shelf plus every stash slot plus every picker row*,
   * and re-binding all of them on every wallet refresh is a lot of listeners
   * for one behaviour. A single listener on the panel overlay catches them all
   * by attribute, and survives any re-render underneath it.
   *
   * ## Why the sheet can buy
   *
   * A detail you have to close before you can act on is a detour. The buy
   * controls carry their own `data-detail-buy-*` attributes rather than
   * reusing the cards' — the cards bind theirs directly, and two handlers on
   * one attribute is how a purchase gets made twice.
   */
  const itemDetailOverlayEl = $<HTMLDivElement>('hub-item-detail-overlay');
  const itemDetailCardEl = $<HTMLDivElement>('hub-item-detail-card');
  const itemDetailBodyEl = $<HTMLDivElement>('hub-item-detail-body');
  const itemDetailTitleEl = $<HTMLHeadingElement>('hub-item-detail-title');
  const itemDetailCloseBtn = $<HTMLButtonElement>('hub-item-detail-close');

  function closeItemDetail(): void {
    itemDetailOverlayEl.hidden = true;
    itemDetailBodyEl.innerHTML = '';
  }

  function showItemDetail(view: ItemDetailView): void {
    itemDetailTitleEl.textContent = view.heading;
    itemDetailBodyEl.innerHTML = itemDetailMarkup(view);
    itemDetailOverlayEl.hidden = false;
    itemDetailCardEl.scrollTop = 0;
    itemDetailCardEl.focus();
  }

  /**
   * A curio's sheet, plus the one thing that can change what to do with it.
   *
   * If today's board wants this exact item, the sheet says so — the patron,
   * the payout, and how close you are. Without it, "should I sell this?" is
   * answered on one screen and quietly contradicted on another.
   */
  function openCurioDetail(itemDefId: string): void {
    const def = ITEM_DEFS[itemDefId];
    if (!def) return;
    const wanted = commissionBoard?.commissions.find((c) => c.itemDefId === itemDefId && !c.delivered);
    showItemDetail(curioDetail(def, { sellWax: sellPrice(def) }, {
      commission: wanted
        ? {
          patron: wanted.patron,
          title: wanted.title,
          count: wanted.count,
          held: wanted.held,
          payoutWax: wanted.payoutWax,
        }
        : undefined,
    }));
  }

  function openConsumableDetail(defId: string): void {
    const def = consumableCatalog.find((c) => c.id === defId);
    if (!def) return;
    const affordable = (balance?.soft ?? 0) >= def.priceWax;
    showItemDetail(consumableDetail(def, {
      owned: consumableStock[def.id] ?? 0,
      taking: plannedSatchel.find((c) => c.defId === def.id)?.charges ?? 0,
      rosterSize: CHARACTER_ROSTER.length,
      action: detailBuyButton('data-detail-buy-consumable', def.id, amountMarkup('wax', def.priceWax), affordable),
      note: t('One charge a press. Charges are account-wide; the satchel is filled from them at the start of a run.'),
    }));
  }

  function openCosmeticDetail(defId: string): void {
    const def = cosmeticCatalog.find((c) => c.id === defId);
    if (!def) return;
    const mine = myCosmetics.find((c) => c.cosmeticDefId === def.id);
    const affordable = (balance?.premium ?? 0) >= def.priceEmber;
    showItemDetail(cosmeticDetail(def, {
      label: cosmeticLabel(def.id, def.name),
      owned: Boolean(mine),
      equipped: mine?.equippedCharacterId != null,
      action: mine
        ? undefined
        : detailBuyButton('data-detail-buy-cosmetic', def.id, amountMarkup('ember', def.priceEmber), affordable),
      note: mine ? t('Owned. Wear it from the Inventory, under the paperdoll.') : undefined,
    }));
  }

  function openArmsDetail(defId: string): void {
    const row = equipmentCatalog.find((r) => r.id === defId);
    if (!row) return;
    const premium = row.currency === 'PREMIUM';
    const wallet = premium ? (balance?.premium ?? 0) : (balance?.soft ?? 0);
    const affordable = row.priceWax !== undefined && wallet >= row.priceWax;
    showItemDetail(armsDetail(row, {
      action: row.owned || row.priceWax === undefined
        ? undefined
        : detailBuyButton('data-detail-buy-arms', row.id, amountMarkup(premium ? 'ember' : 'wax', row.priceWax),
          affordable),
      // The old note claimed every paid arm was appearance only, which stopped
      // being true when the Dagger became the only free one — an Arming Sword
      // really does hit for thirty. It is true again for the Chandlery band and
      // only for it, so that is where the sentence now goes.
      note: row.owned
        ? undefined
        : premium
          ? t('Ember only, and appearance only \u2014 it shares its whole stat block with a piece on the Wax shelf.')
          : undefined,
    }));
  }

  // One listener for every trigger on every shelf. Delegated off the panel
  // overlay because that is the common ancestor of all of them.
  panelOverlayEl.addEventListener('click', (e) => {
    const trigger = (e.target as HTMLElement | null)?.closest<HTMLElement>(
      '[data-detail-curio],[data-detail-consumable],[data-detail-rite],[data-detail-cosmetic],[data-detail-arms]',
    );
    if (!trigger) return;
    e.preventDefault();
    const d = trigger.dataset;
    if (d.detailCurio) openCurioDetail(d.detailCurio);
    else if (d.detailConsumable) openConsumableDetail(d.detailConsumable);
    else if (d.detailRite) openRiteDetail(d.detailRite);
    else if (d.detailCosmetic) openCosmeticDetail(d.detailCosmetic);
    else if (d.detailArms) openArmsDetail(d.detailArms);
  });

  itemDetailBodyEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(
      '[data-detail-buy-consumable],[data-detail-buy-rite],[data-detail-buy-cosmetic],[data-detail-buy-arms]',
    );
    if (!btn || btn.disabled) return;
    const d = btn.dataset;
    // The sheet closes on the way out: what it was describing has just changed
    // — a charge count, an ownership flag — and re-rendering it from stale
    // state would show the player the world as it was before they pressed.
    closeItemDetail();
    if (d.detailBuyConsumable) void onBuyConsumable(d.detailBuyConsumable);
    else if (d.detailBuyRite) void onBuyRite(d.detailBuyRite);
    else if (d.detailBuyCosmetic) void onPurchaseCosmetic(d.detailBuyCosmetic);
    else if (d.detailBuyArms) void onPurchaseArms(d.detailBuyArms);
  });

  itemDetailCloseBtn.addEventListener('click', closeItemDetail);
  itemDetailOverlayEl.addEventListener('click', (e) => {
    if (e.target === itemDetailOverlayEl) closeItemDetail();
  });

  // Escape closes the topmost thing, not everything: with a sheet open over a
  // panel, one press should put you back on the shelf you were reading.
  const onPanelKeydown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    if (!itemDetailOverlayEl.hidden) { closeItemDetail(); return; }
    if (openPanelName) setPanel(null);
  };
  document.addEventListener('keydown', onPanelKeydown);
  /**
   * Shop tabs.
   *
   * The tab strip is re-rendered rather than toggling a class, because it is
   * four buttons and the alternative is two sources of truth for which one is
   * active. Bodies are shown/hidden — they hold live listeners and re-creating
   * them on every tab change would mean re-wiring every button.
   */
  let activeShopTab: ShopTabId = SHOP_TABS[0].id;

  function renderShopTabs(): void {
    shopTabsEl.innerHTML = shopTabsMarkup(activeShopTab);
    shopTabsEl.querySelectorAll<HTMLButtonElement>('button[data-shop-tab]').forEach((btn) => {
      btn.addEventListener('click', () => selectShopTab(btn.dataset.shopTab as ShopTabId));
    });
    root.querySelectorAll<HTMLElement>('[data-shop-body]').forEach((body) => {
      body.hidden = body.dataset.shopBody !== activeShopTab;
    });
  }

  function selectShopTab(tab: ShopTabId): void {
    activeShopTab = tab;
    renderShopTabs();
  }

  /**
   * A header plate was pressed: open wherever that balance is raised.
   *
   * The destination table lives in `ui/balance-plates.ts` alongside the
   * tooltip that promised it, so the two cannot disagree. All this adds is
   * the two hub verbs — open a panel, pick a tab — plus focus.
   *
   * Focus moves to the control that actually does the topping up rather than
   * to the panel card, because the panel is a scrolling column and the thing
   * the player came for is not always the first thing in it. For the Stash
   * that is the Expand button, which sits above the fold but to the right of
   * a meter; for the Shop it is the tab strip, which is where a player who
   * mis-tapped will want to go next anyway.
   */
  function goToTopUp(kind: BalanceKind): void {
    const target = TOP_UP_TARGETS[kind];
    if (target.shopTab) selectShopTab(target.shopTab);
    // The Inventory is painted on open rather than kept live (its own button
    // does the same), so a shortcut that skipped this would show the grid as
    // it was the last time something else repainted it.
    if (target.panel === 'inventory') {
      hideError(inventoryErrEl);
      renderInventory();
    }
    setPanel(target.panel);
    // Slots opens the Inventory, whose own Expand button is what moves that
    // number; there is no tab to focus, and the panel takes focus itself.
    if (target.shopTab) {
      shopTabsEl.querySelector<HTMLButtonElement>(`button[data-shop-tab="${target.shopTab}"]`)?.focus();
    }
  }

  root.querySelectorAll<HTMLButtonElement>('[data-tile]').forEach((btn) => {
    btn.addEventListener('click', () => setPanel(btn.dataset.tile ?? null));
  });

  /**
   * The standings (2026-09-08). See design/gdd/leaderboards.md.
   *
   * Fetched when the panel opens rather than with the rest of the hub: it is
   * the one screen here that is about other people, so nothing else on the hub
   * is waiting on it, and a player who never opens it never pays for it.
   *
   * The chosen mode and sort survive the panel closing. Opening Standings,
   * reading the Arena win-rate board, closing it to check something and
   * reopening on the Delve score board would be the panel forgetting what you
   * were doing.
   */
  const rankingsModesEl = $<HTMLDivElement>('hub-rankings-modes');
  const rankingsSortsEl = $<HTMLDivElement>('hub-rankings-sorts');
  const rankingsErrEl = $<HTMLDivElement>('hub-rankings-error');
  const rankingsStandingEl = $<HTMLDivElement>('hub-rankings-standing');
  const rankingsBodyEl = $<HTMLDivElement>('hub-rankings-body');
  /**
   * The mode catalogue and the win-rate floor, both straight from the server.
   *
   * One nullable object rather than a list plus a number with a literal
   * default, because the default was a copy of a server value — the exact
   * shape that has gone stale five times in this codebase already. There is no
   * second 5 to keep in step now: the panel either has the server's answer or
   * has not drawn yet.
   */
  let leaderboardMeta: { modes: LeaderboardModeRow[]; minMatchesForWinRate: number } | null = null;
  let rankingsMode: LeaderboardModeId = 'DUNGEON';
  let rankingsSort: LeaderboardSort = 'score';
  /**
   * Which fetch is the current one. Flipping tabs faster than the network
   * answers is the normal way to read this panel, and without this the slower
   * of two in-flight boards wins and the tabs show one mode while the rows
   * show another.
   */
  let rankingsRequest = 0;

  /**
   * The board is waiting on the network.
   *
   * Dimmed and inert rather than emptied: switching sort re-ranks the same
   * eleven people, so blanking the list makes the panel flicker through an
   * empty state it is never actually in. The rows stay legible, they just stop
   * being touchable, which is the honest description of what is happening.
   *
   * `aria-busy` because dimming is not an announcement — a screen reader has
   * nothing else here to tell it the list is stale.
   */
  function setRankingsBusy(busy: boolean): void {
    rankingsBodyEl.setAttribute('aria-busy', String(busy));
    rankingsBodyEl.classList.toggle('opacity-50', busy);
    rankingsBodyEl.classList.toggle('pointer-events-none', busy);
  }

  async function renderRankings(): Promise<void> {
    hideError(rankingsErrEl);
    const request = ++rankingsRequest;
    setRankingsBusy(true);
    if (!leaderboardMeta) {
      // First open of the session: nothing is on screen to dim, so say so.
      rankingsBodyEl.innerHTML = `<p class="text-[13px] text-text-dim">${esc(t('Reading the ledger…'))}</p>`;
      try {
        leaderboardMeta = await getLeaderboardModes();
      } catch {
        setRankingsBusy(false);
        rankingsBodyEl.innerHTML = '';
        showError(rankingsErrEl, t('The standings could not be read just now.'));
        return;
      }
      if (request !== rankingsRequest) return;
    }
    const { modes, minMatchesForWinRate } = leaderboardMeta;
    rankingsModesEl.innerHTML = modeTabsMarkup(modes, rankingsMode);
    rankingsSortsEl.innerHTML = sortTabsMarkup(rankingsSort);
    rankingsModesEl.querySelectorAll<HTMLButtonElement>('button[data-lb-mode]').forEach((btn) => {
      btn.addEventListener('click', () => {
        rankingsMode = btn.dataset.lbMode as LeaderboardModeId;
        void renderRankings();
      });
    });
    rankingsSortsEl.querySelectorAll<HTMLButtonElement>('button[data-lb-sort]').forEach((btn) => {
      btn.addEventListener('click', () => {
        rankingsSort = btn.dataset.lbSort as LeaderboardSort;
        void renderRankings();
      });
    });

    try {
      const board = await getLeaderboard(rankingsMode, rankingsSort);
      // A stale answer is dropped without clearing the busy flag: the newer
      // request owns it, and clearing here would un-dim the list while its own
      // fetch is still in the air.
      if (request !== rankingsRequest) return;
      rankingsStandingEl.innerHTML = standingMarkup(board.me, board.sort, minMatchesForWinRate);
      rankingsBodyEl.innerHTML = boardMarkup(board, user.id, minMatchesForWinRate);
      setRankingsBusy(false);
    } catch {
      if (request !== rankingsRequest) return;
      setRankingsBusy(false);
      showError(rankingsErrEl, t('The standings could not be read just now.'));
    }
  }

  /**
   * Talents open for the *selected* delver straight from the alcove. Before
   * this the only way in was a small button inside the Delvers panel, two
   * clicks deep behind a tile — a whole build system the player could not
   * find. It deliberately sits on the hero plate rather than in the tile grid:
   * the tiles are activities and services, this is the character themself.
   */
  talentsBtn.addEventListener('click', () => {
    if (!selectedCharacterId) return;
    tutorial?.signal('talents:opened');
    openTalentManager(selectedCharacterId, refreshCharacters);
  });
  // Same wizard the Delvers panel's "+" opens — one way in, two doors.
  createDelverBtn.addEventListener('click', () => onAddCharacterClick());

  /**
   * The live character stage. Mounted once and kept for the hub's lifetime;
   * `show()` swaps the model, so switching delvers does not rebuild a WebGL
   * context. See render/character-stage.ts for why this is a render and not art.
   */
  const stage = mountCharacterStage(stageHostEl);
  let stageShowing: string | null = null;
  /** Which delver the stage is currently posed as, so switching resets the view. */
  let stageCharacterId: string | null = null;

  /**
   * The skin the server would give **this delver** in a match, or undefined
   * for the base model.
   *
   * Keyed on the character since 2026-09-07, not the class: two knights on one
   * account can look different now, and asking by class would have shown both
   * of them whichever skin was found first.
   *
   * `cosmeticDefId` doubles as the skin asset key (backend
   * cosmetics/config/cosmetic-defs.ts maps them 1:1 today). The frontend's
   * CosmeticItemRow does not carry the key itself, so this coupling is the
   * only way to preview a skin without a new endpoint — and if it ever breaks,
   * character-stage.ts falls back to the base model rather than showing an
   * empty niche.
   */
  function equippedSkinFor(characterId: string): string | undefined {
    return myCosmetics.find((c) => c.equippedCharacterId === characterId)?.cosmeticDefId;
  }

  /** The talent entry point's badge — the unspent count, or nothing. */
  function renderTalentsAffordance(c: CharacterRow | undefined): void {
    talentsBtn.disabled = !c;
    const unspent = c ? c.talentPointsAvailable - c.talentPointsSpent : 0;
    talentsBadgeEl.hidden = unspent <= 0;
    talentsBadgeEl.textContent = String(unspent);
    // The count is decoration for sighted players; the button's own label
    // carries it for anyone who cannot see the badge.
    talentsBtn.setAttribute('aria-label', unspent > 0
      ? tp(unspent, 'Talents — {n} unspent point', 'Talents — {n} unspent points')
      : t('Talents'));
  }

  /**
   * The ids in the seven worn slots, for the stage.
   *
   * Reads `wornGear.worn` rather than the gear catalogue's `owned` flags: the
   * catalogue says what this account *has*, and the stage draws what this
   * delver is *wearing*, which are different questions with the same nouns in
   * them.
   */
  function wornGearIdsOf(): string[] {
    return Object.values(wornGear?.worn ?? {}).filter(Boolean).map((piece) => piece!.id);
  }

  function renderHeroPlate(): void {
    const c = characters.find((x) => x.id === selectedCharacterId);
    renderTalentsAffordance(c);
    renderLoadout();
    // QA 10.10 #14 (2026-10-15): with no delver the plate offered a disabled
    // Talents key and nothing to press. The empty alcove now gets the one
    // action that fills it, where the figure would stand.
    createDelverBtn.hidden = !!c;
    talentsWrapEl.hidden = !c;
    if (!c) {
      heroNameEl.textContent = t('No delver yet');
      heroSubEl.textContent = '';
      heroMetaEl.textContent = t('Choose a class and a name to begin.');
      return;
    }
    heroNameEl.textContent = c.name;
    heroSubEl.textContent = classLevelLine(c.characterClass, c.level);
    const unspent = c.talentPointsAvailable - c.talentPointsSpent;
    const role = roleName(rosterEntry(c.characterClass).role);
    heroMetaEl.textContent = unspent > 0
      ? tp(unspent, '{role} · {n} unspent talent point', '{role} · {n} unspent talent points', { role })
      : role;

    // Switching delvers puts the figure back to facing front. `show()` does
    // that on its own, but only when the model actually changes — two knights
    // share a render, and turning to a new name already spun round would read
    // as the stage having lost track of who it is showing.
    if (c.id !== stageCharacterId) {
      stageCharacterId = c.id;
      stage.resetView();
    }

    const skin = equippedSkinFor(c.id);
    // design/gdd/equipment.md — the arms are part of what the stage shows, so
    // they are part of the key: equipping a greatsword has to reload the
    // figure holding one, and nothing else about the delver changed.
    const mine = armsCharacterId === c.id ? arms : null;
    // Worn gear is part of the key for the same reason the arms are: putting a
    // greathelm on has to reload the figure wearing one. Same guard against a
    // stale fetch, too — `gearCharacterId` is what says the worn set on hand
    // belongs to the delver being drawn and not to the one before them.
    const dressed = gearCharacterId === c.id ? wornGearIdsOf() : [];
    const key = [
      c.characterClass, skin ?? '', mine?.weapon.id ?? '', mine?.offhand?.id ?? '', dressed.join(','),
    ].join('|');
    // Guarded so a re-render for any other reason (a balance refresh, a party
    // event) does not reload a ~340KB glb that is already on screen.
    if (key !== stageShowing) {
      stageShowing = key;
      void stage.show(c.characterClass, skin, {
        ...(mine
          ? { weaponModel: mine.weapon.model, shieldModel: mine.offhand?.model ?? null, weaponClass: mine.weaponClass }
          : {}),
        wornGear: dressed,
      });
    }
  }

  /**
   * The loadout strip under the Talents button.
   *
   * Hidden outright when there is no delver: an empty kit for a character
   * that does not exist is a row of dashes asking to be interpreted.
   */
  function renderLoadout(): void {
    const c = characters.find((x) => x.id === selectedCharacterId);
    if (!c) {
      loadoutEl.hidden = true;
      loadoutEl.innerHTML = '';
      return;
    }
    loadoutEl.hidden = false;
    // Only ever paint a loadout that belongs to the delver on the stage —
    // the fetch is async, so a fast switch can land the previous one.
    const mine = loadout?.characterId === c.id ? loadout : null;
    loadoutEl.innerHTML = loadoutMarkup({
      loadout: mine,
      roster: rosterEntry(c.characterClass),
      loading: loadoutLoading,
    });
  }

  /**
   * Fetched per character rather than per class: capacity carries the
   * character's own `carryCapacityBonusKg`, so two knights with different
   * talent spends genuinely differ.
   */
  async function refreshLoadout(): Promise<void> {
    const characterId = selectedCharacterId;
    if (!characterId) { loadout = null; renderLoadout(); return; }
    loadoutLoading = true;
    renderLoadout();
    try {
      const next = await getLoadout(characterId);
      // A switch mid-flight wins; the stale response is dropped rather than
      // painted over the delver it does not describe.
      if (selectedCharacterId !== characterId) return;
      loadout = next;
    } catch {
      // Non-fatal, same trade as the energy read: the hub still queues, shops
      // and reads quests without the strip.
      if (selectedCharacterId === characterId) loadout = null;
    } finally {
      if (selectedCharacterId === characterId) loadoutLoading = false;
      renderLoadout();
      renderInventory();
    }
  }

  /**
   * The full inventory window.
   *
   * Re-rendered wholesale on every change rather than patched. It is one
   * `innerHTML` of a few dozen small nodes against a user action (a tap, a
   * sale) — not a frame loop — and the alternative is four places that each
   * have to remember to update the capacity bar.
   */
  function renderInventory(): void {
    const character = characters.find((c) => c.id === selectedCharacterId);
    if (!character) {
      inventoryBodyEl.innerHTML = `<p class="text-[13px] text-text-dim">${esc(t('Create a delver to have an inventory.'))}</p>`;
      return;
    }
    const capacity = balance?.stashCapacity ?? 0;
    // Selection can outlive the item it pointed at — selling is the whole
    // point of this pane — so a stale id resolves to nothing rather than
    // leaving a Sell button aimed at a row the server has already deleted.
    if (inventorySelectedId && !stash.some((s) => s.id === inventorySelectedId)) inventorySelectedId = null;

    const sellPrices: Record<string, number> = {};
    for (const item of stash) {
      sellPrices[item.id] = sellPrice({ baseValue: ITEM_DEFS[item.itemDefId]?.baseValue ?? 0 });
    }

    const view: InventoryView = {
      stash,
      capacity,
      loadout: loadout?.characterId === character.id ? loadout : null,
      classId: character.characterClass,
      // Same guard the loadout strip uses: only ever paint arms that belong to
      // the delver on screen, since the fetch is async and a fast switch can
      // land the previous one.
      arms: armsCharacterId === character.id ? arms : null,
      pickerSlot,
      equipmentCatalog,
      // Same guard the arms use: only ever paint gear that belongs to the
      // delver on screen, since the fetch is async and a fast switch can land
      // the previous one.
      worn: gearCharacterId === character.id ? (wornGear?.worn ?? {}) : {},
      gearCatalog,
      gearBonuses: gearCharacterId === character.id ? (wornGear?.bonuses ?? NO_GEAR_BONUSES) : NO_GEAR_BONUSES,
      gearPickerSlot,
      // cosmetics-store.md — the wardrobe half of this window. Skins are
      // filtered to the delver on screen because a skin belongs to one class;
      // emotes are not filtered by anything, because an emote is an account
      // unlock every delver can play.
      // Split on `appliesToClass`, not on a `kind` field — the owned row does
      // not carry one. The wildcard IS the distinction rather than a proxy for
      // it: a cosmetic that belongs to no class is one every delver can use,
      // which is exactly what makes an emote an emote here.
      skins: myCosmetics.filter((c) => c.appliesToClass === character.characterClass),
      // Which of them is on THIS delver — the row's own id, not a boolean, so
      // a skin worn by another character of the same class shows as available
      // rather than as worn here. Picking it moves it, which is the rule.
      wornByCharacterId: character.id,
      emotes: myCosmetics.filter((c) => c.appliesToClass === '*'),
      skinPickerOpen,
      cosmeticNames: Object.fromEntries(cosmeticCatalog.map((d) => [d.id, cosmeticLabel(d.id, d.name)])),
      selectedId: inventorySelectedId,
      characterName: character.name,
      characterSub: classLevelLine(character.characterClass, character.level),
      sellPrices,
      expandLabel: balance
        ? `<span class="inline-flex items-center gap-1">${esc(t('Expand (+{n})', { n: 10 }))}${amountMarkup('wax', expandStashCost(balance.stashCapacity), { hideLabel: true })}</span>`
        : esc(t('Expand')),
      expandDisabled: !balance,
    };
    inventoryBodyEl.innerHTML = inventoryMarkup(view);

    inventoryBodyEl.querySelectorAll<HTMLButtonElement>('button[data-inv-slot]').forEach((btn) => {
      btn.addEventListener('click', () => {
        // An empty slot clears the selection, which is the only way out of the
        // detail pane without picking something else.
        inventorySelectedId = btn.dataset.invSlot || null;
        renderInventory();
      });
    });
    inventoryBodyEl.querySelectorAll<HTMLButtonElement>('button[data-inv-sell]').forEach((btn) => {
      btn.addEventListener('click', () => onSellFromInventory(btn.dataset.invSell!));
    });
    inventoryBodyEl.querySelectorAll<HTMLButtonElement>('button[data-inv-sell-all]').forEach((btn) => {
      btn.addEventListener('click', () => onSellStackFromInventory((btn.dataset.invSellAll ?? '').split(',').filter(Boolean)));
    });
    inventoryBodyEl.querySelector<HTMLButtonElement>('button[data-inv-expand]')
      ?.addEventListener('click', () => onExpandFromInventory());
    inventoryBodyEl.querySelectorAll<HTMLButtonElement>('button[data-doll-slot]').forEach((btn) => {
      const slot = btn.dataset.dollSlot as EquipmentSlot | '';
      if (!slot) return;
      // Opening one picker closes the other: they share a column.
      btn.addEventListener('click', () => { gearPickerSlot = null; pickerSlot = slot; renderInventory(); });
    });
    inventoryBodyEl.querySelectorAll<HTMLButtonElement>('button[data-doll-gear]').forEach((btn) => {
      const slot = btn.dataset.dollGear as GearSlot | '';
      if (!slot) return;
      btn.addEventListener('click', () => { pickerSlot = null; gearPickerSlot = slot; renderInventory(); });
    });
    inventoryBodyEl.querySelector<HTMLButtonElement>('button[data-gear-cancel]')
      ?.addEventListener('click', () => { gearPickerSlot = null; renderInventory(); });
    // Opening one picker closes the others: all three share the right column.
    inventoryBodyEl.querySelectorAll<HTMLButtonElement>('button[data-skin-open]').forEach((btn) => {
      btn.addEventListener('click', () => {
        pickerSlot = null; gearPickerSlot = null; skinPickerOpen = true; renderInventory();
      });
    });
    inventoryBodyEl.querySelector<HTMLButtonElement>('button[data-skin-cancel]')
      ?.addEventListener('click', () => { skinPickerOpen = false; renderInventory(); });
    inventoryBodyEl.querySelectorAll<HTMLButtonElement>('button[data-skin-pick]').forEach((btn) => {
      btn.addEventListener('click', () => onPickSkin(btn.dataset.skinPick ?? ''));
    });
    inventoryBodyEl.querySelectorAll<HTMLButtonElement>('button[data-equip-gear]').forEach((btn) => {
      btn.addEventListener('click', () => onEquipGear(btn.dataset.equipGear || null));
    });
    inventoryBodyEl.querySelectorAll<HTMLButtonElement>('button[data-buy-gear]').forEach((btn) => {
      btn.addEventListener('click', () => onPurchaseGear(btn.dataset.buyGear!));
    });
    inventoryBodyEl.querySelector<HTMLButtonElement>('button[data-equip-cancel]')
      ?.addEventListener('click', () => { pickerSlot = null; renderInventory(); });
    inventoryBodyEl.querySelectorAll<HTMLButtonElement>('button[data-equip-choice]').forEach((btn) => {
      btn.addEventListener('click', () => onEquip(btn.dataset.equipChoice || null));
    });
    inventoryBodyEl.querySelectorAll<HTMLButtonElement>('button[data-buy-arms]').forEach((btn) => {
      btn.addEventListener('click', () => onPurchaseArms(btn.dataset.buyArms!));
    });
  }

  /**
   * Unlock a paid arms piece, then equip it in the same gesture.
   *
   * Buying something you are looking at in a picker and then having to find
   * and press it again would be a second step for no decision — the purchase
   * *is* the choice. Mirrors the contracts panel, where a freshly bought
   * contract selects itself.
   */
  async function onPurchaseArms(defId: string): Promise<void> {
    hideError(inventoryErrEl);
    try {
      await purchaseArms(defId);
      equipmentCatalog = await getEquipmentCatalog();
      renderArmsShelf();
      await refreshBalance();
      await onEquip(defId);
    } catch (err) {
      showError(inventoryErrEl, errorMessage(err));
      renderInventory();
    }
  }

  /**
   * design/gdd/equipment.md — set one slot.
   *
   * Closes the picker on success and refreshes the loadout, because equipping
   * moves the weight, the damage and the delver's own hands: the strip under
   * the Talents button, the paperdoll and the stage all read this.
   */
  /**
   * Wear a piece, or empty the slot with `null`.
   *
   * The loadout strip is refreshed afterwards rather than patched: a swap
   * moves armour, health, stamina, carry capacity and start weight at once,
   * and every one of those is computed server-side.
   */
  async function onEquipGear(defId: string | null): Promise<void> {
    if (!gearPickerSlot || !selectedCharacterId) return;
    hideError(inventoryErrEl);
    const slot = gearPickerSlot;
    const characterId = selectedCharacterId;
    try {
      const next = await equipGear(characterId, slot, defId);
      // The fetch is async and a fast delver switch can land the previous
      // one's result on the current one's doll.
      if (selectedCharacterId !== characterId) return;
      wornGear = next;
      gearCharacterId = characterId;
      gearPickerSlot = null;
      // The stage is wearing the old set until this lands — same repaint the
      // weapon swap does two functions down, and for the same reason.
      renderHeroPlate();
      await refreshLoadout();
    } catch (err) {
      showError(inventoryErrEl, errorMessage(err));
    }
    renderInventory();
  }

  /**
   * Buy a tier-1 piece, then wear it in the same gesture.
   *
   * Mirrors `onPurchaseArms`: buying something you are looking at in a picker
   * and then having to find and press it again would be a second step for no
   * decision — the purchase *is* the choice.
   */
  async function onPurchaseGear(defId: string): Promise<void> {
    hideError(inventoryErrEl);
    try {
      await purchaseGear(defId);
      gearCatalog = await getGearCatalog();
      await Promise.all([refreshBalance(), onEquipGear(defId)]);
    } catch (err) {
      showError(inventoryErrEl, errorMessage(err));
      renderInventory();
    }
  }

  async function refreshGear(): Promise<void> {
    const characterId = selectedCharacterId;
    if (!characterId) return;
    try {
      const [catalog, worn] = await Promise.all([getGearCatalog(), getWornGear(characterId)]);
      if (selectedCharacterId !== characterId) return;
      gearCatalog = catalog;
      wornGear = worn;
      gearCharacterId = characterId;
      renderInventory();
      renderGearShelf();
      // Selling a worn piece takes it off the delver server-side, so the
      // figure on the plinth is a set behind until this runs.
      renderHeroPlate();
    } catch (err) {
      showError(inventoryErrEl, errorMessage(err));
    }
  }

  async function onEquip(defId: string | null): Promise<void> {
    if (!pickerSlot || !selectedCharacterId) return;
    hideError(inventoryErrEl);
    const slot = pickerSlot;
    const characterId = selectedCharacterId;
    try {
      const next = await equipItem(characterId, slot, defId);
      if (selectedCharacterId !== characterId) return;
      arms = next;
      armsCharacterId = characterId;
      pickerSlot = null;
      // The stage is holding the old weapon until this lands.
      stageShowing = null;
      renderHeroPlate();
      await refreshLoadout();
    } catch (err) {
      showError(inventoryErrEl, errorMessage(err));
    }
    renderInventory();
  }

  /** Fetched per character: the stored choice is per character, not per class. */
  async function refreshArms(): Promise<void> {
    const characterId = selectedCharacterId;
    if (!characterId) { arms = null; armsCharacterId = null; renderInventory(); return; }
    try {
      const next = await getEquipment(characterId);
      if (selectedCharacterId !== characterId) return;
      arms = next;
      armsCharacterId = characterId;
      // The stage was framed for whatever the class default was.
      stageShowing = null;
      renderHeroPlate();
    } catch {
      // Non-fatal, same trade as the energy read: the hub still works without
      // the paperdoll's contents.
      if (selectedCharacterId === characterId) { arms = null; armsCharacterId = null; }
    }
    renderInventory();
  }

  async function refreshEquipmentCatalog(): Promise<void> {
    try {
      equipmentCatalog = await getEquipmentCatalog();
      renderArmsShelf();
      renderInventory();
    } catch {
      // The picker simply has nothing to offer; the doll still draws what is equipped.
    }
  }

  async function onSellFromInventory(stashItemId: string): Promise<void> {
    hideError(inventoryErrEl);
    try {
      await sellItem(stashItemId);
      inventorySelectedId = null;
      await Promise.all([refreshBalance(), refreshStash()]);
    } catch (err) {
      showError(inventoryErrEl, errorMessage(err));
    }
  }

  /**
   * Selling a whole stack (2026-09-05).
   *
   * One request per row, because `sellItem` is one row — a stack is a way of
   * drawing the stash, not a way of storing it, and inventing a bulk endpoint
   * for a display grouping would put the grouping into the database.
   *
   * Sequential rather than `Promise.all`: each sale credits Wax and the server
   * is the one counting, so firing six at once at a wallet is how a race gets
   * written. Six requests is also the realistic upper bound for a 25-slot
   * stash of one item.
   *
   * The refresh happens in `finally`. A stack that fails on its fourth row has
   * still sold three, and a panel that kept showing all six would be lying
   * about what the player owns.
   */
  async function onSellStackFromInventory(ids: readonly string[]): Promise<void> {
    hideError(inventoryErrEl);
    if (ids.length === 0) return;
    const first = stash.find((row) => row.id === ids[0]);
    const unit = sellPrice({ baseValue: ITEM_DEFS[first?.itemDefId ?? '']?.baseValue ?? 0 });
    const itemName = ITEM_DEFS[first?.itemDefId ?? '']?.name;
    // Item names are the item catalogue's keys (marked where they are defined).
    const label = itemName ? t(itemName) : t('these');
    // The single Sell has no gate; this one does. The size of the mistake is
    // what makes a gate worth its friction, and this is the only control on
    // the screen that can destroy six things with one tap.
    if (!confirmSell(t('all {n} × {item}', { n: ids.length, item: label }), unit * ids.length, 'wax')) return;
    try {
      for (const id of ids) await sellItem(id);
      inventorySelectedId = null;
    } catch (err) {
      showError(inventoryErrEl, errorMessage(err));
    } finally {
      await Promise.all([refreshBalance(), refreshStash()]);
    }
  }

  async function onExpandFromInventory(): Promise<void> {
    hideError(inventoryErrEl);
    try {
      await expandStash();
      await refreshBalance();
    } catch (err) {
      showError(inventoryErrEl, errorMessage(err));
    }
  }

  /** WoW-style portrait rail. Same state as the panel list — one source, two views. */
  /**
   * The slot block under the roster: the count, and the one button that
   * applies — create while there is room, buy when there is not.
   *
   * Re-rendered rather than toggled, so the two states cannot both be live at
   * once. Listeners are rebound each time because the markup is replaced.
   */
  function renderCharacterSlots(): void {
    characterSlotsEl.innerHTML = characterSlotsMarkup({
      // `used` comes from the roster this screen already has, not from the
      // server's snapshot: creating or deleting a delver changes it, and a
      // count that needed its own request would lag one action behind.
      slots: characterSlots && { ...characterSlots, used: characters.length },
      ember: balance?.premium ?? 0,
      busy: buyingSlot,
    });
    characterSlotsEl.querySelector<HTMLButtonElement>('#hub-character-add-btn')
      ?.addEventListener('click', onAddCharacterClick);
    characterSlotsEl.querySelector<HTMLButtonElement>('#hub-character-slot-buy')
      ?.addEventListener('click', onBuyCharacterSlot);
  }

  async function onBuyCharacterSlot(): Promise<void> {
    if (buyingSlot) return;
    hideError(characterErrEl);
    buyingSlot = true;
    renderCharacterSlots();
    try {
      // The purchase returns the new counts, so there is no follow-up read.
      characterSlots = await buyCharacterSlot();
      await refreshBalance();
    } catch (err) {
      showError(characterErrEl, errorMessage(err));
    } finally {
      buyingSlot = false;
      renderCharacterSlots();
    }
  }

  async function refreshCharacterSlots(): Promise<void> {
    try {
      characterSlots = await getCharacterSlots();
      renderCharacterSlots();
    } catch {
      // Non-fatal: the create button stays offered and the server refuses if
      // the roster is actually full.
    }
  }

  function renderRosterRail(): void {
    const ROLE_PIP: Record<string, string> = { TANK: 'bg-[#8fb8e8]', HEALER: 'bg-[#7fd8a4]', DPS: 'bg-[#e39a72]' };
    rosterRailEl.innerHTML = characters.map((c) => {
      const selected = c.id === selectedCharacterId;
      return `
        <button data-rail="${c.id}" title="${escapeHtml(t('{name} — {class}', { name: c.name, class: titleCase(classDisplayName(c.characterClass)) }))}" aria-pressed="${selected}"
          class="roster-medal relative shrink-0 w-[52px] h-[52px] rounded-full border-2 grid place-items-center overflow-visible transition-colors
            ${selected ? 'border-gold-bright shadow-[0_0_0_3px_rgba(255,184,0,0.13),0_0_18px_rgba(255,184,0,0.22)]' : 'border-border hover:border-gold/45'}">
          <img src="/assets/ui/lobby/portrait-${c.characterClass}.png" alt="${escapeHtml(t('{name}, {class}', { name: c.name, class: titleCase(classDisplayName(c.characterClass)) }))}"
            class="w-full h-full rounded-full object-cover object-[50%_12%] bg-surface-2 ${selected ? '' : 'opacity-75'}" />
          <span class="absolute -right-px -bottom-px w-[15px] h-[15px] rounded-full border-2 border-bg ${ROLE_PIP[rosterEntry(c.characterClass).role] ?? 'bg-border'}"></span>
        </button>`;
    }).join('');
    rosterRailEl.querySelectorAll<HTMLButtonElement>('[data-rail]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.dataset.rail === selectedCharacterId) return;
        selectedCharacterId = btn.dataset.rail!;
        renderCharacters();
        renderQueueUi();
      });
    });
  }

  // design/gdd/character-creation.md — a Character replaces the old ephemeral
  // class picker; queueing now selects which Character to bring, not a class.
  function renderCharacters(): void {
    if (characters.length === 0) {
      characterListEl.innerHTML = `<p class="text-[13px] text-text-dim">${esc(t('No characters yet — create one below.'))}</p>`;
    } else {
      if (!selectedCharacterId || !characters.some((c) => c.id === selectedCharacterId)) {
        // The remembered one first, and only validated against the list that
        // actually came back — a character deleted on another tab, or on
        // another machine, must not leave the hub pointing at a ghost.
        const remembered = readSelectedCharacter();
        selectedCharacterId = remembered && characters.some((c) => c.id === remembered)
          ? remembered
          : characters[0].id;
      }
      // Persisted here rather than at the four places that assign the
      // selection — the rail, the list, the delete path and the fallback above
      // all funnel through this render, so one write cannot be forgotten by a
      // fifth caller added later.
      writeSelectedCharacter(selectedCharacterId);
      characterListEl.innerHTML = characters.map((c) => `
        <div data-select="${c.id}" class="flex items-center justify-between gap-2 px-3 py-2 rounded border cursor-pointer
            ${c.id === selectedCharacterId ? 'border-gold bg-gold/10' : 'border-border bg-surface-2 hover:border-gold/50'}">
          <div class="min-w-0">
            <p class="text-sm capitalize">${escapeHtml(c.name)} <span class="text-[11px] text-text-dim">${esc(t('Lv{n} {class}', { n: c.level, class: classDisplayName(c.characterClass) }))}</span></p>
            <p class="text-[11px] text-text-dim">${esc(tp(c.talentPointsAvailable - c.talentPointsSpent, '{n} unspent talent point', '{n} unspent talent points'))}</p>
          </div>
          <div class="flex gap-1.5 shrink-0">
            <button data-talents="${c.id}" class="btn btn-stone">${esc(t('Talents'))}</button>
            <button data-delete="${c.id}" class="btn btn-danger">${esc(t('Delete'))}</button>
          </div>
        </div>
      `).join('');
      characterListEl.querySelectorAll<HTMLElement>('[data-select]').forEach((el) => {
        el.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).closest('[data-delete]') || (e.target as HTMLElement).closest('[data-talents]')) return;
          selectedCharacterId = el.dataset.select!;
          renderCharacters();
          renderQueueUi();
        });
      });
      characterListEl.querySelectorAll<HTMLButtonElement>('[data-delete]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          onDeleteCharacter(btn.dataset.delete!);
        });
      });
      characterListEl.querySelectorAll<HTMLButtonElement>('[data-talents]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          openTalentManager(btn.dataset.talents!, refreshCharacters);
        });
      });
    }
    renderCharacterSlots();
    //
    // The rail and the stage are painted from the same call, so the panel list
    // and the lobby can never disagree about who is selected.
    renderRosterRail();
    renderHeroPlate();
    renderQueueUi();
    /**
     * Quick-use consumables — the satchel is class-filtered, so switching
     * delver changes what the shop shows and what the preview says you will
     * carry.
     *
     * Guarded on the class rather than fetched on every render: this function
     * runs for a rename or a talent spend too, and a request per render would
     * be a request per keystroke in the worst case. Re-rendered locally either
     * way, so switching between two delvers of the *same* class is instant.
     */
    const selectedClass = characters.find((c) => c.id === selectedCharacterId)?.characterClass ?? null;
    if (selectedClass !== satchelClass) {
      satchelClass = selectedClass;
      void refreshConsumables();
    } else {
      renderSatchel();
    }
    // The loadout is per *character*, not per class: capacity carries this
    // delver's own talent bonus, so switching between two knights still
    // refetches.
    if (selectedCharacterId !== loadoutCharacterId) {
      loadoutCharacterId = selectedCharacterId;
      loadout = null;
      pickerSlot = null;
      gearPickerSlot = null;
      void refreshLoadout();
      void refreshArms();
      void refreshGear();
    }
  }

  async function refreshCharacters(): Promise<void> {
    try {
      const before = characters.length;
      characters = await listCharacters();
      renderCharacters();
      renderPlayConfirm();
      if (characters.length > before) tutorial?.signal('character:created');
      tutorial?.updateContext({ characterCount: characters.length });
      // A talent spend or a respec can move carry capacity, and both land
      // here. `renderCharacters` only refetches on a *switch*, so this is the
      // path that catches a change to the delver already selected.
      if (selectedCharacterId === loadoutCharacterId) void refreshLoadout();
    } catch (err) {
      showError(characterErrEl, errorMessage(err));
    }
  }

  function onAddCharacterClick(): void {
    hideError(characterErrEl);
    openCharacterCreationWizard(refreshCharacters);
  }

  async function onDeleteCharacter(id: string): Promise<void> {
    hideError(characterErrEl);
    try {
      await deleteCharacter(id);
      if (selectedCharacterId === id) selectedCharacterId = null;
      await refreshCharacters();
    } catch (err) {
      showError(characterErrEl, errorMessage(err));
    }
  }

  /**
   * The stash as a grid with a fill meter, not a list of rows.
   *
   * A stash is scanned ("anything worth selling?") and it is capacity-bound —
   * neither of which a text list shows you. Selling lives here rather than in
   * the Shop because this is where the things you own are; two screens both
   * claiming to hold your loot would be one too many.
   */
  /**
   * The stash changed, so the one screen that draws it repaints.
   *
   * **The Stash panel and its hub tile are gone (2026-09-07).** User: *"I
   * think Stash here is not needed if its in inventory."* Right: the Inventory
   * window's right-hand half already *was* the stash — the same rows, the same
   * `stackStash` grouping, the same sell action and its own Expand button
   * beside its own capacity meter. Two screens claiming to hold your loot was
   * one too many, and the panel's own comment had already said so about the
   * Shop ("two screens both claiming to hold your loot would be one too many")
   * without noticing it was the second screen.
   *
   * What is left is this function, which exists only so that every caller that
   * changes the stash — a sale, an expansion, a run ending — has one thing to
   * call rather than each remembering to repaint the inventory.
   */
  function renderStash(): void {
    renderInventory();
  }

  /**
   * Quick-use consumables (2026-08-25) — the satchel shop.
   *
   * Filtered to the *selected* delver's class, because a row you cannot use is
   * worse than no row: rage classes have nothing to restore (see
   * consumable-defs.ts) and would otherwise be offered a tonic they can never
   * drink. Stock itself is account-level, so switching delvers changes which
   * rows are shown, never what you own.
   */
  function renderSatchel(): void {
    const character = characters.find((c) => c.id === selectedCharacterId);
    const carried = plannedSatchel.reduce((sum, c) => sum + c.charges, 0);
    setTileSub('shop', carried > 0 ? t('{n} in satchel', { n: carried }) : t('Satchel & skins'));

    if (!character) {
      satchelNoteEl.textContent = t('Select a delver to see what they can carry.');
      satchelListEl.innerHTML = '';
      return;
    }

    const usable = consumableCatalog
      .filter((def) => def.usableBy.includes(character.characterClass))
      .sort((a, b) => a.slot - b.slot);

    satchelNoteEl.textContent = carried > 0
      ? tp(carried, '{name} will carry {n} charge into the next run. Lost on death.', '{name} will carry {n} charges into the next run. Lost on death.', { name: character.name })
      : t('Bought here, carried into a run, and lost if you die down there.');


    const wax = balance?.soft ?? 0;
    satchelListEl.innerHTML = gridMarkup(usable.map((def) => consumableCardMarkup(def, {
      owned: consumableStock[def.id] ?? 0,
      taking: plannedSatchel.find((c) => c.defId === def.id)?.charges ?? 0,
      effectLine: consumableEffectLine(def),
      affordable: wax >= def.priceWax,
    })), t('Nothing this delver can use.'));

    satchelListEl.querySelectorAll<HTMLButtonElement>('button[data-buy-consumable]').forEach((btn) => {
      btn.addEventListener('click', () => onBuyConsumable(btn.dataset.buyConsumable!));
    });
  }

  /**
   * One line describing what a charge does, computed from the server's own
   * `effect`/`magnitude`/`durationMs` rather than written out here.
   *
   * Same split the talent tree settled on (`engine/config/talent-copy.ts`):
   * names are copy, every *number* comes from the server. A rebalance in
   * `consumable-defs.ts` changes this text with no edit here — mirroring server
   * values by hand is how the HUD once shipped "110 / 100".
   */

  async function refreshConsumables(): Promise<void> {
    try {
      const character = characters.find((c) => c.id === selectedCharacterId);
      const data = await getConsumables(character?.characterClass);
      consumableCatalog = data.catalog;
      consumableStock = data.stock;
      plannedSatchel = data.satchel;
      renderSatchel();
    } catch (err) {
      showError(satchelErrEl, errorMessage(err));
    }
  }

  async function onBuyConsumable(defId: string): Promise<void> {
    hideError(satchelErrEl);
    try {
      // One charge per press. A quantity stepper would be the obvious
      // alternative, but a charge is 10-12 Wax against a run's income of a few
      // dozen — the interesting decision is "one more or not", pressed a few
      // times, not a number typed into a box.
      await purchaseConsumable(defId, 1);
      await Promise.all([refreshBalance(), refreshConsumables(), refreshLoadout()]);
    } catch (err) {
      showError(satchelErrEl, errorMessage(err));
    }
  }

  /**
   * design/gdd/rites.md — the Rites shelf, under the potions on the same tab.
   *
   * Same tab rather than its own, deliberately: both are things you buy at the
   * Sconce and take into a run, and a player who has found the satchel has
   * found this. What separates them is a heading note stating the terms, which
   * is the fix `6c469f2` applied to the cosmetics shelves after the satchel's
   * counting-down cards taught a player that emotes were consumable too. One
   * shop, three ownership models, each shelf now saying which it is.
   *
   * Not filtered by class — unlike the potions, which hide a tonic a rage
   * class can never drink. Every Rite works for every delver, so there is
   * nothing to hide and no row here that anybody cannot use.
   */
  function renderRites(): void {
    ritesShelfEl.innerHTML = shelfHeadingMarkup(t('Rites'), t(RITE_SHELF_NOTE));
    const wax = balance?.soft ?? 0;
    ritesListEl.innerHTML = gridMarkup(riteCatalog.map((def) => riteCardMarkup(def, {
      owned: riteStock[def.id] ?? 0,
      selected: selectedRiteDefId === def.id,
      effectLine: riteEffectLine(def),
      affordable: wax >= def.priceWax,
    })), t('Nothing on the shelf.'));

    ritesListEl.querySelectorAll<HTMLButtonElement>('button[data-buy-rite]').forEach((btn) => {
      btn.addEventListener('click', () => void onBuyRite(btn.dataset.buyRite!));
    });
    ritesListEl.querySelectorAll<HTMLButtonElement>('button[data-select-rite]').forEach((btn) => {
      btn.addEventListener('click', () => void onSelectRite(btn.dataset.selectRite!));
    });
  }

  /**
   * The Play panel's picker — one owned Rite, or none.
   *
   * Lives beside the contract because it is the same kind of decision: a Wax
   * commitment made before the door opens that changes what the run is worth
   * rather than what you are. It shows **only what is owned**, because a
   * picker offering something you would have to go and buy is a shop pretending
   * to be a choice — the shelf two tiles away is where buying happens.
   *
   * "Burn nothing" is always present and is not a disabled state: choosing to
   * carry no Rite is a real choice, and the row that says so is what stops a
   * player believing the last one they bought is stuck to them.
   */
  function renderPlayRites(): void {
    const owned = riteCatalog.filter((def) => (riteStock[def.id] ?? 0) > 0);
    if (owned.length === 0) {
      playRiteNoteEl.textContent = t('None owned. The Sconce sells them on the Satchel shelf — one is burned per run.');
      playRitesEl.innerHTML = '';
      return;
    }
    playRiteNoteEl.textContent = t('One is burned when you descend, whatever happens after.');
    const rows = [
      riteChoiceRow(null, selectedRiteDefId === null),
      ...owned.map((def) => riteChoiceRow(def, selectedRiteDefId === def.id)),
    ];
    playRitesEl.innerHTML = rows.join('');
    playRitesEl.querySelectorAll<HTMLButtonElement>('button[data-play-rite]').forEach((btn) => {
      const value = btn.dataset.playRite!;
      btn.addEventListener('click', () => void onSelectRite(value === '' ? null : value));
    });
  }

  /** One row of the picker. Written per branch because Tailwind v4 extracts class names statically. */
  function riteChoiceRow(def: RiteDefRow | null, selected: boolean): string {
    const cls = selected
      ? 'w-full min-h-11 text-left px-3 py-2 rounded frame-inset border border-gold/70 flex items-center gap-2'
      : 'w-full min-h-11 text-left px-3 py-2 rounded frame-inset border border-border flex items-center gap-2 hover:border-gold/40';
    const mark = selected ? '<span class="text-gold shrink-0">&#9679;</span>' : '<span class="text-text-dim shrink-0">&#9675;</span>';
    if (!def) {
      return `<button data-play-rite="" class="${cls}">${mark}`
        + `<span class="min-w-0"><span class="block text-[13px]">${esc(t('Burn nothing'))}</span>`
        + `<span class="block text-[11px] text-text-dim">${esc(t('Keep what you own for another run.'))}</span></span></button>`;
    }
    const held = riteStock[def.id] ?? 0;
    return `<button data-play-rite="${esc(def.id)}" class="${cls}">${mark}`
      + `<span class="min-w-0"><span class="block text-[13px] truncate">${esc(t(def.name))}`
      + `<span class="text-text-dim"> \u00b7 ${esc(t('{n} held', { n: held }))}</span></span>`
      + `<span class="block text-[11px] text-text-dim truncate">${esc(riteConditionLine(def))}</span></span></button>`;
  }

  async function refreshRites(): Promise<void> {
    try {
      const data = await getRites();
      riteCatalog = data.catalog;
      riteStock = data.stock;
      selectedRiteDefId = data.selectedRiteDefId;
      renderRites();
      renderPlayRites();
    } catch (err) {
      showError(ritesErrEl, errorMessage(err));
    }
  }

  async function onBuyRite(defId: string): Promise<void> {
    hideError(ritesErrEl);
    try {
      // One per press, like a satchel charge, and for a sharper reason: you
      // burn one a run, so "one more" is the only quantity that ever answers
      // a real question.
      await purchaseRite(defId, 1);
      await Promise.all([refreshBalance(), refreshRites()]);
    } catch (err) {
      showError(ritesErrEl, errorMessage(err));
    }
  }

  /**
   * Choosing is optimistic in neither direction: the server owns the
   * selection (it re-checks ownership) and the answer it returns is what gets
   * rendered, so a refused choice never leaves a mark on the UI that the next
   * run would not honour.
   */
  async function onSelectRite(defId: string | null): Promise<void> {
    hideError(ritesErrEl);
    try {
      const res = await selectRite(defId);
      selectedRiteDefId = res.selectedRiteDefId;
      renderRites();
      renderPlayRites();
    } catch (err) {
      showError(ritesErrEl, errorMessage(err));
    }
  }

  function openRiteDetail(defId: string): void {
    const def = riteCatalog.find((r) => r.id === defId);
    if (!def) return;
    const affordable = (balance?.soft ?? 0) >= def.priceWax;
    showItemDetail(riteDetail(def, {
      owned: riteStock[def.id] ?? 0,
      selected: selectedRiteDefId === def.id,
      action: detailBuyButton('data-detail-buy-rite', def.id, amountMarkup('wax', def.priceWax), affordable),
      note: t('One Rite is burned per run. Choose which on the Play panel before you queue.'),
    }));
  }

  /**
   * design/gdd/insurance.md — capacity drawn as slots, with the rule stated.
   *
   * Replaces a single sentence and a button. Insurance is the one mechanic in
   * the game whose value a player only discovers by dying, so the panel now
   * says what a slot does, where it is actually used, and how many exist —
   * rather than leaving all three to be found out the expensive way.
   */
  function renderInsurance(): void {
    if (!insurance) return;
    insuranceStatusEl.textContent = t(INSURANCE_DESCRIPTION);
    // Cover first, capacity second: capacity is permanent and never urgent,
    // cover is the half that runs out.
    insuranceCoverEl.innerHTML = insuranceCoverMarkup(insurance);
    insuranceCoverEl.querySelector<HTMLButtonElement>('#hub-insurance-buy-cover')
      ?.addEventListener('click', onBuyInsuranceCover);
    insuranceSlotsEl.innerHTML = insuranceSlotsMarkup(insurance);
    insuranceFactsEl.innerHTML = insuranceFactsMarkup(insurance);
    setTileSub('insurance', insuranceTileSub(insurance));
    const expand = insuranceExpandLabel(insurance);
    insuranceExpandBtn.innerHTML = expand.html;
    insuranceExpandBtn.disabled = expand.disabled;
  }

  /**
   * design/gdd/contracts.md Core Rule #6 (amended 2026-09-01) — the map
   * picker. Every floor is a card, including the free one: selecting Cellars
   * is how a player stops spending a contract, which automatic-deepest-wins
   * gave them no way to do.
   */
  /**
   * Repaint every contract card in the hub (2026-09-07).
   *
   * The same cards are drawn in two places — the Contracts tile and the Play
   * panel's first step — and `onSelectContract` repainted only the first. So
   * pressing "Run this" on a card in the Play panel, which is where a player
   * actually stands, changed the server and left the card exactly as it was.
   * That is the second half of *"it does not respond to my selection of the
   * map"*, and it was a live bug independent of the purchase cap.
   *
   * One function rather than two calls at each site, so the next caller cannot
   * forget the other shelf.
   */
  function renderContractCards(): void {
    renderMaps();
    renderPlayPanel();
  }

  function renderMaps(): void {
    const owned = myContracts.filter((c) => c.status === 'AVAILABLE' && c.usesRemaining > 0);
    setTileSub('contracts', owned.length > 0 ? tp(owned.length, '{n} owned', '{n} owned') : t('Deeper entry'));

    paintMapPicker(contractsCatalogEl, 'contracts', renderMaps);
    renderDestination();
  }

  /**
   * Where "Play" is about to take you. Without it the button looks identical
   * whether it is about to spend a contract charge or nothing at all. It now
   * lives inside the launch caption rather than in a line of its own.
   */
  function renderDestination(): void {
    renderLaunchCaption();
  }

  /**
   * Skins and emotes, one grid, two shelves.
   *
   * Both shelves state their terms (2026-09-07). A player asked for the emote
   * cards to say "emotes can be used 5 times and then disappear" — which no
   * emote does. Nothing in the game charges for an emote twice. What is
   * charge-based is the **satchel**, two tabs along, where a card counts down
   * as you spend it; with nothing on the cosmetics shelf saying otherwise, the
   * countable model was the one that carried over. Saying it outright is
   * cheaper than a player finding out either way by accident.
   *
   * Replaces the old catalog-list-plus-owned-list pair: a thing you own and
   * the same thing in the shop were two rows in two places, and the only
   * difference between them was which button they carried. One card per
   * cosmetic now carries its own state — buy, equip, equipped — the same way
   * the map picker's cards do.
   */
  function renderCosmetics(): void {
    const ember = balance?.premium ?? 0;
    const stateFor = (defId: string, appliesToClass: string) => {
      const owned = myCosmetics.find((c) => c.cosmeticDefId === defId);
      return {
        owned: !!owned,
        // "Worn by somebody", since the shelf has no delver in mind. Which
        // delver is a wardrobe question, and the wardrobe is in the Inventory.
        equipped: owned?.equippedCharacterId != null,
        affordable: ember >= (cosmeticCatalog.find((d) => d.id === defId)?.priceEmber ?? 0),
        // Off the owned row, not the catalogue: the refund is priced against
        // what this player paid for it.
        buybackEmber: owned?.buybackEmber,
        appliesToClass,
      };
    };

    const skins = cosmeticCatalog.filter((d) => d.kind !== 'EMOTE');
    const emotes = cosmeticCatalog.filter((d) => d.kind === 'EMOTE');

    const section = (heading: string, note: string, defs: typeof cosmeticCatalog, empty: string): string =>
      shelfHeadingMarkup(heading, note)
      + gridMarkup(defs.map((def) => cosmeticCardMarkup(def, stateFor(def.id, String(def.appliesToClass)))), empty);

    cosmeticsCatalogEl.innerHTML = cosmeticCatalog.length === 0
      ? `<p class="text-[13px] text-text-dim">${esc(t('Nothing in stock yet.'))}</p>`
      : section(
        t('Skins'), t(SKIN_SHELF_NOTE), skins, t('No skins available yet.'),
      ) + section(
        t('Emotes'), t(EMOTE_SHELF_NOTE), emotes, t('No emotes available yet.'),
      );

    cosmeticsCatalogEl.querySelectorAll<HTMLButtonElement>('button[data-buy-cosmetic]').forEach((btn) => {
      btn.addEventListener('click', () => onPurchaseCosmetic(btn.dataset.buyCosmetic!));
    });
    cosmeticsCatalogEl.querySelectorAll<HTMLButtonElement>('button[data-sell-cosmetic]').forEach((btn) => {
      btn.addEventListener('click', () => onSellCosmetic(btn.dataset.sellCosmetic!));
    });
  }

  /**
   * design/gdd/quests.md — assignment happens lazily on first dungeon join,
   * not registration, so an empty list is expected until then.
   *
   * Every row states what the quest pays (2026-09-05). It used to be printed
   * only inside the Claim button, so a player found out what a quest was worth
   * one moment after the number could still change anything they did.
   * Claim-eligible (COMPLETED) rows keep the button in that column, because
   * there the amount and the action are the same gesture.
   */
  function renderQuests(): void {
    // The tile shows what is *actionable*, not the total: "2 to claim" is a
    // reason to open the panel, "5 quests" is not.
    const claimable = myQuests.filter((q) => q.status === 'COMPLETED').length;
    setTileSub('quests', claimable > 0 ? t('{n} to claim', { n: claimable }) : t('{n} active', { n: myQuests.length }));
    if (myQuests.length === 0) {
      questsListEl.innerHTML = `<p class="text-[13px] text-text-dim">${esc(t('No quests yet — join a run to get your first assignment.'))}</p>`;
      return;
    }
    questsListEl.innerHTML = myQuests.map((uq) => {
      const completed = uq.status === 'COMPLETED';
      // '' for a quest that pays nothing, so the column disappears rather than
      // heading an empty space with the word "Reward".
      const reward = questRewardMarkup(uq.quest);
      const kind = questKindStyle(uq.quest.questType);
      // A carved plaque (2026-10-09): the quest's seal, an edge in its kind's
      // colour, and a gold glow once there is something to claim.
      return `
        <div class="row-plaque flex flex-wrap sm:flex-nowrap items-center justify-between gap-x-3 gap-y-2 pl-2.5 pr-3 py-2.5" style="--row-accent:${kind.accent}"${completed ? ' data-ready' : ''}>
          <img src="${kind.seal}" alt="" aria-hidden="true" width="160" height="160" draggable="false"
               class="quest-seal w-11 h-11 sm:w-12 sm:h-12 shrink-0 select-none drop-shadow-[0_3px_6px_rgba(0,0,0,0.6)]" />
          <div class="min-w-0 flex-1 flex flex-col gap-1.5">
            <p class="text-sm flex flex-wrap items-center gap-x-2 gap-y-1"><span class="font-heading text-[17px] leading-tight text-text">${escapeHtml(t(uq.quest.name))}</span> <span class="shrink-0 font-heading font-bold text-[12px] px-1.5 py-0.5 rounded-sm border" style="color:${kind.accent};border-color:${kind.accent}66;background:${kind.accent}14">${esc(kind.label)}</span></p>
            <p class="text-[11px] text-text-dim truncate">${escapeHtml(t(uq.quest.description))}</p>
            ${questBarMarkup(uq.progress, uq.quest.targetCount, completed)}
          </div>
          ${completed
            ? `<button data-claim-quest="${uq.id}" class="btn btn-gold shrink-0">${esc(t('Claim'))} ${questRewardMarkup(uq.quest, { hideLabel: true, tone: 'text-bg' })}</button>`
            : reward
              ? `<div class="basis-full sm:basis-auto shrink-0 flex sm:flex-col items-center sm:items-end gap-2 sm:gap-0.5 sm:text-right pl-14 sm:pl-0">
                   <span class="eyebrow eyebrow-dim">${esc(t('Reward'))}</span>
                   <span class="text-[13px]">${reward}</span>
                 </div>`
              : ''}
        </div>
      `;
    }).join('');
    questsListEl.querySelectorAll<HTMLButtonElement>('button[data-claim-quest]').forEach((btn) => {
      btn.addEventListener('click', () => { void onClaimQuest(btn.dataset.claimQuest!).catch(() => { /* shown in the panel's error line */ }); });
    });
  }

  /** design/gdd/pvp-arena.md Core Rule #3 — ARENA_DUO/ARENA_TRIO are sized identically to their PvE counterparts. */
  function squadSize(mode: SquadMode): number {
    // By suffix, so WAVES_DUO/WAVES_TRIO size like the rest (they read as 1).
    return mode.endsWith('DUO') ? 2 : mode.endsWith('TRIO') ? 3 : 1;
  }
  function isArenaMode(mode: SquadMode): boolean {
    return mode.startsWith('ARENA_');
  }
  function partyRequiredSize(): number {
    return squadSize(squadMode);
  }
  function partyReadyToQueue(): boolean {
    if (squadSize(squadMode) === 1) return true;
    return myParty !== null && myParty.leaderId === user.id && myParty.memberIds.length === partyRequiredSize();
  }

  function renderQueueUi(): void {
    // Idle no longer queues — it opens the Play flow — so it is only
    // disabled for the states where pressing it can do nothing at all. The
    // reasons a run cannot start (no character, party not full, no energy)
    // are stated inside the flow, on the control that would act on them.
    queueBtn.disabled = queueState === 'found' || queueState === 'ready_check';
    // A visible pulse while queued — the searching-text ticker alone updates
    // once a second, easy to miss; motion on the button itself confirms
    // something is actively happening during the up-to-45s silent server wait.
    queueBtn.classList.toggle('animate-pulse', queueState === 'queued');
    if (queueState === 'queued') {
      queueBtn.textContent = t('Leave Queue');
    } else if (queueState === 'ready_check') {
      queueBtn.textContent = t('Match found — respond above');
    } else if (queueState === 'found') {
      queueBtn.textContent = t('Match found…');
    } else {
      queueBtn.textContent = t('Play');
    }
    renderPlayConfirm();
  }

  /**
   * The panel's own button carries every reason a run cannot start, because
   * it is the one that would start it: no character, an unfilled party, an
   * unbuilt mode, an empty energy bar.
   */
  function renderPlayConfirm(): void {
    const mode = gameModes.find((m) => m.id === selectedGameMode);
    const cta = playCtaMarkup({ mode, size: selectedSquadSize, energy, queueing: queueState !== 'idle' });
    if (!cta.disabled && selectedCharacterId === null) {
      playConfirmBtn.textContent = t('Create a delver first');
      playConfirmBtn.disabled = true;
      paintLaunch(false);
      return;
    }
    if (!cta.disabled && squadSize(squadMode) > 1 && !partyReadyToQueue()) {
      playConfirmBtn.textContent = myParty
        ? t('Waiting for party ({count}/{size})', { count: myParty.memberIds.length, size: partyRequiredSize() })
        : t('Invite {n} more', { n: partyRequiredSize() - 1 });
      playConfirmBtn.disabled = true;
      paintLaunch(false);
      return;
    }
    playConfirmBtn.innerHTML = cta.html;
    playConfirmBtn.disabled = cta.disabled;
    paintLaunch(!cta.disabled);
  }

  /**
   * The hub's Play, while idle: "Hold to play" when a hold would start the
   * run, and plain "Play" when it would not — a tap then opens the panel,
   * whose own button says why (no delver, no energy, a mode not built).
   */
  function paintLaunch(ready: boolean): void {
    launchReady = ready;
    if (queueState !== 'idle') return;
    queueBtn.textContent = ready ? t('Hold to play') : t('Play');
    queueBtn.toggleAttribute('data-ready', ready);
  }

  function startQueueSearchTimer(immediate = true): void {
    if (queueSearchStartedAt === null) queueSearchStartedAt = Date.now();
    searchingOverlayEl.hidden = false;
    if (queueSearchTimer) return; // already ticking
    if (immediate) updateQueueSearchingText();
    queueSearchTimer = setInterval(updateQueueSearchingText, 1000);
  }
  function stopQueueSearchTimer(): void {
    if (queueSearchTimer) { clearInterval(queueSearchTimer); queueSearchTimer = null; }
    queueSearchStartedAt = null;
    searchingOverlayEl.hidden = true;
  }
  function updateQueueSearchingText(): void {
    if (queueState !== 'queued' || queueSearchStartedAt === null) return;
    const elapsed = Math.floor((Date.now() - queueSearchStartedAt) / 1000);
    // Never tells the player about bots (bot-delvers.md Core Rule #5): a
    // dungeon is one team of four and the seats nobody queued for are filled
    // for them, which the player only needs to hear as "your party".
    const arena = isArenaMode(squadMode);
    queueStatusEl.textContent = t('Searching for a match… {n}s', { n: elapsed });
    // The overlay (a real, unmissable full-screen stage, closer to the
    // CS:GO-style search screen originally asked for) mirrors the same
    // ticker — the inline hub text above stays too, for anyone who dismisses
    // the overlay's own visual weight isn't there to reassure them further.
    searchingElapsedEl.textContent = t('{n}s', { n: elapsed });
    searchingNoteEl.textContent = arena
      ? t('Looking for other delvers to duel…')
      : t('Finding your party…');
  }

  function renderReadyCheckOverlay(): void {
    if (!activeReadyCheck) {
      readyCheckOverlayEl.hidden = true;
      if (readyCheckCountdownTimer) { clearInterval(readyCheckCountdownTimer); readyCheckCountdownTimer = null; }
      return;
    }
    readyCheckOverlayEl.hidden = false;
    readyCheckRosterEl.textContent = tp(activeReadyCheck.roster.length, '{n} delver ready to enter', '{n} delvers ready to enter');

    const updateCountdown = () => {
      const secondsLeft = readyCheckSecondsLeft(readyCheckExpiresAt, Date.now());
      readyCheckCountdownEl.textContent = t('{n}s', { n: secondsLeft });
    };
    updateCountdown();
    if (readyCheckCountdownTimer) clearInterval(readyCheckCountdownTimer);
    readyCheckCountdownTimer = setInterval(updateCountdown, 250);
  }

  async function refreshBalance(): Promise<void> {
    try {
      balance = await getBalance();
      renderBalance();
      // Balance and stash arrive from two independent requests, and both the
      // Inventory's capacity meter (`stashCapacity`) and the shop's
      // affordability dimming (the wallet) read across them. Whichever lands
      // second has to repaint the other's view, or the meter renders blank
      // forever — it did, for one build, back when this was its own panel.
      renderStash();
      renderGearShelf();
      renderArmsShelf();
      renderSatchel();
      renderCosmetics();
      renderEnergyShop();
      renderWaxShop();
      renderCharacterSlots();
      renderInventory();
    } catch (err) {
      // Reported on the Inventory's own error line since the Stash panel was
      // removed: that is the only screen that draws any of this now.
      showError(inventoryErrEl, errorMessage(err));
    }
  }

  async function refreshStash(): Promise<void> {
    try {
      stash = await listStash();
      renderStash();
    } catch (err) {
      showError(inventoryErrEl, errorMessage(err));
    }
  }

  async function refreshInsurance(): Promise<void> {
    try {
      insurance = await getInsuranceStatus();
      renderInsurance();
      tutorial?.updateContext({ insuranceCharges: insurance.charges });
    } catch (err) {
      showError(insuranceErrEl, errorMessage(err));
    }
  }

  function renderEnergyShop(): void {
    energyStatusEl.innerHTML = energyLineMarkup(energy);
    const ember = balance?.premium ?? 0;
    energyBundlesEl.innerHTML = gridMarkup(
      energyBundles.map((b) => energyBundleCardMarkup(b, ember >= b.priceEmber)),
      t('No bundles available.'),
    );
    energyBundlesEl.querySelectorAll<HTMLButtonElement>('button[data-buy-energy]').forEach((btn) => {
      btn.addEventListener('click', () => onPurchaseEnergy(btn.dataset.buyEnergy!));
    });
  }

  async function onPurchaseEnergy(bundleId: string): Promise<void> {
    hideError(energyErrEl);
    try {
      await purchaseEnergyBundle(bundleId);
      await Promise.all([refreshBalance(), refreshEnergy()]);
      renderEnergyShop();
    } catch (err) {
      showError(energyErrEl, errorMessage(err));
    }
  }

  /**
   * The Ember tab.
   *
   * Rendered once per offers fetch, and then never again while the player is
   * using it — every subsequent change is written into the four dependent
   * controls in place by `syncEmberCustom`.
   *
   * The first version of this re-rendered on every keystroke and restored the
   * caret afterwards. `setSelectionRange` throws on `input[type=number]`,
   * which killed the handler halfway and left the field holding a
   * concatenation of what the player typed and what it had before. Not
   * re-rendering is both the fix and the simpler thing.
   */
  function renderEmberShop(): void {
    if (!emberOffers) return;
    methodPickerHandle?.destroy();
    methodPickerHandle = null;
    currencySelectorHandle?.destroy();
    currencySelectorHandle = null;
    emberBodyEl.innerHTML = emberStorefrontMarkup(emberOffers, emberCustomCents);
    mountEmberPaymentWidgets();
    emberBodyEl.querySelectorAll<HTMLButtonElement>('button[data-buy-ember]').forEach((btn) => {
      btn.addEventListener('click', () => onBuyEmber(Number(btn.dataset.buyEmber), btn.dataset.packId));
    });
    const range = emberBodyEl.querySelector<HTMLInputElement>('#hub-ember-range');
    const amount = emberBodyEl.querySelector<HTMLInputElement>('#hub-ember-amount');
    range?.addEventListener('input', () => setEmberCustom(Number(range.value), true));
    amount?.addEventListener('input', () => setEmberCustom(Number(amount.value) * 100, false));
    amount?.addEventListener('blur', () => setEmberCustom(emberCustomCents, true));
  }

  function mountEmberPaymentWidgets(): void {
    if (!paymentMethodsData) return;

    const currencyEl = emberBodyEl.querySelector<HTMLElement>('#hub-ember-currency');
    const methodsEl = emberBodyEl.querySelector<HTMLElement>('#hub-ember-methods');
    if (!currencyEl || !methodsEl) return;

    const methods = paymentMethodsData.deposit;
    const currencies = paymentMethodsData.currencies;

    if (currencies.length > 1) {
      currencySelectorHandle = mountCurrencySelector(currencyEl, {
        currencies,
        value: selectedCurrency,
        methods,
        onChange(currency) {
          selectedCurrency = currency;
          methodPickerHandle?.setCurrency(currency);
        },
      });
    }

    methodPickerHandle = mountPaymentMethodPicker(methodsEl, {
      methods,
      currency: selectedCurrency,
      selected: selectedMethod,
      billingComplete: billingView?.complete,
      billingHref: '/settings/profile',
      showDisabled: false,
      onSelect(method) {
        selectedMethod = method.id;
      },
      onCurrencySwitch(currency) {
        selectedCurrency = currency;
        currencySelectorHandle?.setValue(currency);
      },
    });

    const patchPickerLayout = () => {
      const group = methodsEl.querySelector<HTMLElement>('[role="radiogroup"]');
      if (group) { group.classList.remove('flex-col'); group.classList.add('flex-row', 'flex-wrap'); }
      methodsEl.querySelectorAll('img').forEach((img) => img.remove());
      methodsEl.querySelectorAll<HTMLElement>('button[data-method]').forEach((btn) => {
        btn.classList.remove('w-full');
        btn.classList.add('flex-1', 'min-w-[80px]', 'justify-center');
      });
    };
    patchPickerLayout();
    const patchObserver = new MutationObserver(patchPickerLayout);
    patchObserver.observe(methodsEl, { childList: true, subtree: true });
    patchPickerLayout();
  }

  /**
   * One number, one setter — the slider, the field, the preview and the buy
   * button are four controls over a single amount, and this is the only thing
   * that moves it.
   *
   * `syncField` is false only while the field itself is the source, so that
   * the player's own text is left alone mid-entry.
   */
  function setEmberCustom(cents: number, syncField: boolean): void {
    if (!emberOffers || !Number.isFinite(cents)) return;
    emberCustomCents = clampCents(cents, emberOffers.custom);

    const range = emberBodyEl.querySelector<HTMLInputElement>('#hub-ember-range');
    const amount = emberBodyEl.querySelector<HTMLInputElement>('#hub-ember-amount');
    const preview = emberBodyEl.querySelector<HTMLElement>('#hub-ember-preview');
    const buy = emberBodyEl.querySelector<HTMLButtonElement>('#hub-ember-buy-custom');

    if (range) range.value = String(emberCustomCents);
    if (amount && syncField) amount.value = (emberCustomCents / 100).toFixed(2);
    if (preview) preview.textContent = emberPreviewText(emberOffers.emberPerEur, emberCustomCents);
    if (buy) {
      buy.dataset.buyEmber = String(emberCustomCents);
      buy.textContent = t('Buy {price}', { price: formatEur(emberCustomCents) });
    }
  }

  /**
   * Hand off to the provider.
   *
   * `location.assign` rather than a new tab: a popup is what a browser blocks,
   * and the checkout sends the player back to /play either way (the success
   * and cancel URLs are the server's). Nothing is credited here — Ember
   * arrives on the webhook, so there is no optimistic balance to write.
   */
  async function onBuyEmber(eurCents: number, packId?: string): Promise<void> {
    hideError(emberErrEl);
    if (!Number.isFinite(eurCents) || eurCents <= 0) return;
    const buyButtons = emberBodyEl.querySelectorAll<HTMLButtonElement>('button[data-buy-ember]');
    buyButtons.forEach((b) => { b.disabled = true; b.classList.add('opacity-60', 'pointer-events-none'); });
    try {
      const deposit = await createEmberDeposit({
        ...(packId ? { productId: packId } : { amountCents: eurCents }),
        method: selectedMethod,
        currency: selectedCurrency,
      });
      window.location.assign(deposit.redirectUrl);
    } catch (err) {
      buyButtons.forEach((b) => { b.disabled = false; b.classList.remove('opacity-60', 'pointer-events-none'); });
      showError(emberErrEl, emberErrorMessage(err));
    }
  }

  /**
   * The two refusals this endpoint has, said in words.
   *
   * The server throws machine codes so that callers can branch on them; a
   * player reading `ember_purchases_unavailable` in a red bar is being shown
   * an internal identifier. Anything unrecognised falls through to the shared
   * handler, which is still better than inventing a sentence for it.
   */
  function emberErrorMessage(err: unknown): string {
    const raw = errorMessage(err);
    if (raw.includes('PAYMENTS_NOT_ADOPTED') || raw.includes('ember_purchases_unavailable')) return t('Ember purchases are not open yet.');
    if (raw.includes('AMOUNT_OUT_OF_RANGE') || raw.includes('amount_out_of_range')) return t('That amount is outside what the shop can sell.');
    if (raw.includes('UNKNOWN_PRODUCT') || raw.includes('offer_unavailable') || raw.includes('offer_amount_mismatch')) return t('This offer has just ended.');
    if (raw.includes('BILLING_ADDRESS_REQUIRED')) return t('Set your billing address in Settings before purchasing.');
    if (raw.includes('KYC_REQUIRED')) return t('Identity verification is required for this amount.');
    if (raw.includes('EMAIL_NOT_VERIFIED')) return t('Verify your email address before purchasing.');
    if (raw.includes('CHECKOUT_FAILED') || raw.includes('TOO_MANY_PENDING')) return t('Could not start the payment — please try again.');
    return raw;
  }

  /**
   * Coming back from a checkout.
   *
   * Core deposits return to `/play?order=<orderId>`. Legacy checkouts used
   * `?purchase=ok|cancelled` — both are handled so in-flight sessions land
   * correctly.
   *
   * Ember is credited by the webhook, which is a separate request, so the
   * balance is re-read and the toast says "shortly" rather than claiming an
   * amount that might not have landed. Parameters are stripped so a refresh
   * does not replay the message.
   */
  function handleCheckoutReturn(): void {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('order');
    const outcome = params.get('purchase');
    if (!orderId && !outcome) return;
    params.delete('order');
    params.delete('purchase');
    const query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));

    if (orderId) {
      clearPendingOrder();
      void getDeposit(orderId).then((order) => {
        if (order.status === 'DECLINED' || order.status === 'ERROR' || order.status === 'EXPIRED') {
          toasts.show({ heading: t('Payment'), title: t('Payment did not go through'), detail: t('Nothing was charged. Try again or pick a different method.') });
        } else if (order.status === 'APPROVED' || order.credited) {
          void refreshBalance();
          toasts.show({ heading: t('Payment'), title: t('Thank you — your payment went through'), detail: t('Ember lands in your balance as soon as the payment clears, usually within a moment.') });
        } else {
          toasts.show({ heading: t('Payment'), title: t('Payment status pending'), detail: t('If you completed the payment, Ember will appear in your balance shortly.') });
        }
      }).catch(() => {
        toasts.show({ heading: t('Payment'), title: t('Payment status unknown'), detail: t('Check your balance in a moment — if the payment went through, Ember will appear.') });
      });
    } else if (outcome === 'ok') {
      void refreshBalance();
      toasts.show({ heading: t('Payment'), title: t('Thank you — your payment went through'), detail: t('Ember lands in your balance as soon as the payment clears, usually within a moment.') });
    } else if (outcome === 'cancelled') {
      toasts.show({ heading: t('Payment'), title: t('Purchase cancelled'), detail: t('Nothing was charged.') });
    }
  }

  async function refreshEmberOffers(): Promise<void> {
    try {
      const [offers, methodsRes, billingRes] = await Promise.all([
        getEmberOffers(),
        getPaymentMethods().catch(() => null),
        getBillingAddress().catch(() => null),
      ]);
      emberOffers = offers;
      emberCustomCents = emberOffers.packs[0]?.eurCents ?? emberOffers.custom.minCents;
      if (methodsRes) {
        paymentMethodsData = methodsRes;
        selectedCurrency = methodsRes.defaultCurrency;
        const firstEnabled = methodsRes.deposit[0];
        if (firstEnabled) selectedMethod = firstEnabled.id;
      }
      if (billingRes) billingView = billingRes;
      renderEmberShop();
    } catch (err) {
      showError(emberErrEl, errorMessage(err));
    }
  }

  /** design/gdd/energy.md — re-read after anything that spends or grants a unit. */
  async function refreshEnergy(): Promise<void> {
    try {
      energy = await getEnergy();
      renderBalance();
      renderPlayPanel();
      renderEnergyShop();
    } catch {
      // Non-fatal: the flow still works, it just cannot show the balance.
      // Failing the whole hub over an energy read would be a worse trade.
    }
  }

  async function refreshEnergyBundles(): Promise<void> {
    try {
      energyBundles = await getEnergyBundles();
      renderEnergyShop();
    } catch (err) {
      showError(energyErrEl, errorMessage(err));
    }
  }

  async function refreshGameModes(): Promise<void> {
    try {
      gameModes = await getGameModes();
      // A wire mode may already be set (a reconnect, an auto-queue link), so
      // the picker adopts it rather than resetting the player's choice.
      adoptSquadMode(squadMode);
      renderPlayPanel();
    } catch (err) {
      showError(playErrEl, errorMessage(err));
    }
  }

  async function refreshContracts(): Promise<void> {
    try {
      const [catalog, mine] = await Promise.all([getContractCatalog(), getMyContracts()]);
      contractCatalog = catalog;
      myContracts = mine.contracts;
      selectedContractId = mine.selectedContractId;
      renderContractCards();
    } catch (err) {
      showError(contractsErrEl, errorMessage(err));
    }
  }

  /**
   * Optimistic on purpose: the picker repaints immediately and the request
   * confirms behind it, because this control is pressed while comparing two
   * floors and a round-trip of dead UI per tap reads as broken. A failure
   * re-reads the server's answer rather than keeping the optimistic one.
   */
  async function onSelectContract(contractId: string | null): Promise<void> {
    hideError(contractsErrEl);
    hideError(playContractsErrEl);
    const previous = selectedContractId;
    selectedContractId = contractId;
    renderContractCards();
    try {
      await selectContract(contractId);
    } catch (err) {
      selectedContractId = previous;
      renderContractCards();
      const code = err instanceof ApiError ? err.message : (err as Error)?.message;
      const message = contractErrorMessage(code, errorMessage(err));
      showError(contractsErrEl, message);
      showError(playContractsErrEl, message);
    }
  }

  async function onExpandInsurance(): Promise<void> {
    hideError(insuranceErrEl);
    try {
      await expandInsurance();
      await Promise.all([refreshBalance(), refreshInsurance()]);
    } catch (err) {
      showError(insuranceErrEl, errorMessage(err));
    }
  }

  /** Buys one run of cover. One at a time: it is cheap, and a quantity picker for a 150-Wax item is more screen than the decision deserves. */
  async function onBuyInsuranceCover(): Promise<void> {
    hideError(insuranceErrEl);
    try {
      await buyInsuranceCharges(1);
      await Promise.all([refreshBalance(), refreshInsurance()]);
    } catch (err) {
      showError(insuranceErrEl, errorMessage(err));
    }
  }

  /**
   * Buy a contract.
   *
   * Failures land in **both** error slots and always refresh (2026-09-07).
   * This panel's only error line lives below the mode picker and the format
   * pills, so a refusal on a card at the top of a scrolling panel was printed
   * somewhere the player never scrolled to — which is why a working button was
   * reported as doing nothing. The refresh is the other half: whatever the
   * server refused for, re-reading the allowance makes the shelf show it.
   */
  async function onPurchaseContract(contractDefId: string): Promise<void> {
    hideError(contractsErrEl);
    hideError(playContractsErrEl);
    try {
      await purchaseContract(contractDefId);
      await Promise.all([refreshBalance(), refreshContracts()]);
    } catch (err) {
      const code = err instanceof ApiError ? err.message : (err as Error)?.message;
      const message = contractErrorMessage(code, errorMessage(err));
      showError(contractsErrEl, message);
      showError(playContractsErrEl, message);
      await refreshContracts();
    }
  }

  /**
   * The commission board.
   *
   * Re-rendered wholesale rather than patched after a delivery: a delivery
   * moves the wallet, the stash and one card's state at once, and the "held"
   * counts on every card are read off the stash server-side.
   */
  function renderCommissions(): void {
    setTileSub('commissions', boardTileSub(commissionBoard));
    commissionsBodyEl.innerHTML = commissionBoardMarkup(commissionBoard, new Date());
    commissionsBodyEl.querySelectorAll<HTMLButtonElement>('button[data-deliver-commission]').forEach((btn) => {
      btn.addEventListener('click', () => onDeliverCommission(btn.dataset.deliverCommission!));
    });
  }

  async function refreshCommissions(): Promise<void> {
    try {
      commissionBoard = await getCommissions();
      renderCommissions();
    } catch (err) {
      showError(commissionsErrEl, errorMessage(err));
    }
  }

  async function onDeliverCommission(defId: string): Promise<void> {
    hideError(commissionsErrEl);
    try {
      await deliverCommission(defId);
      await Promise.all([refreshBalance(), refreshStash(), refreshCommissions()]);
    } catch (err) {
      showError(commissionsErrEl, errorMessage(err));
    }
  }

  /**
   * The Shop's Arms shelf (2026-09-04).
   *
   * Arms were buyable long before this and only from the Inventory paperdoll —
   * you had to open a weapon slot to discover a shelf existed at all. The Shop
   * already sold worn gear from its own tab, so the asymmetry was not a
   * decision: a player looking for something to spend Wax on simply never
   * found ten of the things they could spend it on.
   *
   * Filtered to the paid pieces. The free ones come with the account, and a
   * shelf row reading "Owned" for something nobody ever bought is noise — the
   * picker is where you see the whole catalogue side by side.
   *
   * Buying here does not equip, for the same reason the gear shelf does not:
   * there is no open slot to put it in, and changing what a delver is holding
   * from a shop tab is a change they never saw happen.
   */
  /**
   * The one gate in front of every Sell button (2026-09-04).
   *
   * `talent-manager-modal.ts` already uses a native confirm for the respec, so
   * this is the hub's established shape for "you are about to spend or destroy
   * something". A bespoke overlay would look better and would be the fourth
   * modal in a screen that already has three.
   *
   * It always names the *loss* first and the payment second, because the
   * payment is the part that already looks appealing on the button. The
   * unbuyable case gets a sentence of its own — a Vault piece sold is a piece
   * gone until you find another, and that is not the same decision as selling
   * something the shelf will hand straight back.
   */
  function confirmSell(what: string, price: number, currency: 'wax' | 'ember', opts: {
    unbuyable?: boolean; worn?: boolean;
  } = {}): boolean {
    const lines = [currency === 'ember'
      ? t('Sell {item} for {price} Ember?', { item: what, price })
      : t('Sell {item} for {price} Wax?', { item: what, price })];
    // Said before rather than reported after. The server takes the piece off
    // every delver holding it — including one on a character you are not
    // looking at — and a warning is only worth anything while the decision is
    // still open.
    if (opts.worn) lines.push(t('Any delver equipped with it will put it down.'));
    if (opts.unbuyable) lines.push(t('Nothing sells it back to you. The only other one is down in the dark.'));
    return confirm(lines.join('\n\n'));
  }

  function renderArmsShelf(): void {
    const waxBalance = balance?.soft ?? 0;
    const card = (row: EquipmentCatalogRow): string => armsShelfCardMarkup({
      id: row.id,
      name: row.name,
      slot: row.slot === 'WEAPON' ? (row.hands === 2 ? t('Two-handed') : t('One-handed')) : t('Off-hand'),
      hands: row.hands,
      weightKg: row.totalWeightKg,
      priceWax: row.priceWax,
      buybackWax: row.buybackWax,
      currency: row.currency,
      owned: row.owned,
      // Damage, swing and reach only. The card is a quarter of a panel wide and
      // the first pass appended stamina, which pushed every weapon's line into
      // an ellipsis — a truncated stat line is worse than a shorter one,
      // because you cannot tell which number got cut. The picker carries the
      // full block, and so does the detail sheet.
      statLine: row.slot === 'WEAPON'
        ? t('{dmg} dmg · {swing}s · {reach}m', { dmg: row.damage ?? 0, swing: formatNumber((row.swingMs ?? 0) / 1000, 2), reach: formatNumber(row.reachM ?? 0) })
        : t('Carried weight only'),
      blurb: row.blurb,
      equipped: arms?.weapon.id === row.id || arms?.offhand?.id === row.id,
      // Checked against the wallet the card actually spends from (2026-09-07).
      // Dimming a 40-Ember dirk because the Wax balance is low would say the
      // wrong thing about a purchase that would in fact go through.
    }, (row.currency === 'PREMIUM' ? (balance?.premium ?? 0) : waxBalance) >= (row.priceWax ?? 0));

    // Everything, in two halves (2026-09-06). User: "from here can we list all
    // items in the shop? I see less gear in shop then in inventory." The shelf
    // listed the eighteen priced arms and silently omitted the thirteen every
    // delver already owns — half the picker, missing, with no explanation for
    // why. They are here now under their own heading, which is also the
    // clearest way to say what a paid arm actually buys: the free half is
    // right above it with the same numbers.
    //
    // Three sections since 2026-09-07, when the Chandlery band arrived. The
    // Ember arms get their own heading rather than sitting mixed into the Wax
    // list, because the coin on the button is a small thing to notice and
    // "this costs money" is a large thing to have missed. The heading also
    // does the honest work the cards cannot: it says outright that nothing
    // down there is stronger than what is above it.
    const wax = equipmentCatalog.filter((row) => row.currency === 'SOFT');
    const ember = equipmentCatalog.filter((row) => row.currency === 'PREMIUM');
    const free = equipmentCatalog.filter((row) => row.priceWax === undefined);
    armsListEl.innerHTML = shelfHeadingMarkup(t('For sale \u2014 everything a delver can hold'))
      + gridMarkup(wax.map(card), t('Nothing on the shelf yet.'))
      + (ember.length === 0 ? '' : shelfHeadingMarkup(
        t('The Chandlery \u2014 Ember only, and every one a re-cut of something above'))
        + gridMarkup(ember.map(card), ''))
      + (free.length === 0 ? '' : shelfHeadingMarkup(t('Yours from the start \u2014 the one you always have'))
        + gridMarkup(free.map(card), ''));

    armsListEl.querySelectorAll<HTMLButtonElement>('button[data-buy-arms-shop]').forEach((btn) => {
      btn.addEventListener('click', () => onBuyArmsFromShop(btn.dataset.buyArmsShop!));
    });
    armsListEl.querySelectorAll<HTMLButtonElement>('button[data-sell-arms]').forEach((btn) => {
      btn.addEventListener('click', () => onSellArms(btn.dataset.sellArms!));
    });
    armsListEl.querySelectorAll<HTMLButtonElement>('button[data-equip-arms-shop]').forEach((btn) => {
      btn.addEventListener('click', () => onEquipArmsFromShop(btn.dataset.equipArmsShop!));
    });
  }

  /**
   * Equip an arm from the Shop's own shelf (2026-09-06), the twin of
   * `onEquipGearFromShop`. The slot comes off the def, because a shelf card
   * knows what it is and there is no open picker to infer one from.
   */
  async function onEquipArmsFromShop(defId: string): Promise<void> {
    const row = equipmentCatalog.find((r) => r.id === defId);
    if (!row || !selectedCharacterId) return;
    hideError(armsErrEl);
    const characterId = selectedCharacterId;
    try {
      arms = await equipItem(characterId, row.slot, defId);
      if (selectedCharacterId !== characterId) return;
      armsCharacterId = characterId;
      renderHeroPlate();
      await refreshLoadout();
      renderArmsShelf();
      renderInventory();
    } catch (err) {
      showError(armsErrEl, errorMessage(err));
    }
  }

  async function onBuyArmsFromShop(defId: string): Promise<void> {
    hideError(armsErrEl);
    try {
      await purchaseArms(defId);
      equipmentCatalog = await getEquipmentCatalog();
      // Balance after the catalogue: the shelf dims what you cannot afford, so
      // repainting before the wallet lands shows the old affordability for a frame.
      await refreshBalance();
      renderArmsShelf();
      renderInventory();
    } catch (err) {
      showError(armsErrEl, errorMessage(err));
    }
  }

  /**
   * Sell a paid weapon or shield back.
   *
   * Repaints the paperdoll as well as the shelf, and has to: selling clears
   * the piece from every delver holding it, so the doll two panels over would
   * otherwise keep drawing a weapon that is not there. The warning about that
   * is in the confirm, before the decision, rather than in a line of text
   * after it.
   */
  async function onSellArms(defId: string): Promise<void> {
    hideError(armsErrEl);
    const row = equipmentCatalog.find((r) => r.id === defId);
    if (!row || row.buybackWax === undefined) return;
    // Named, not assumed: a Chandlery arm refunds Ember, and a confirm that
    // said "Wax" would be describing a different transaction from the one
    // about to happen.
    if (!confirmSell(t(row.name), row.buybackWax, row.currency === 'PREMIUM' ? 'ember' : 'wax',
      { worn: true })) return;
    try {
      await sellArms(defId);
      equipmentCatalog = await getEquipmentCatalog();
      await refreshBalance();
      renderArmsShelf();
      // Re-read what the delver is holding: the sale cleared the choice
      // server-side, and `refreshArms` is what repaints the stage as well as
      // the doll. It ends in `renderInventory` itself.
      await refreshArms();
    } catch (err) {
      showError(armsErrEl, errorMessage(err));
    }
  }

  /**
   * Sell a worn piece back.
   *
   * The deep tiers are the reason this exists and the reason it confirms: no
   * shelf sells them back at any price, and until the buyback counter opened a
   * duplicate was worth *nothing at all* — `grantMany` keeps one row per
   * piece, so the chest that held the second one was a chest wasted.
   */
  async function onSellGear(defId: string): Promise<void> {
    hideError(gearErrEl);
    const row = gearCatalog.find((r) => r.id === defId);
    if (!row) return;
    if (!confirmSell(t(row.name), row.buybackWax, 'wax', {
      worn: true, unbuyable: row.priceWax === undefined,
    })) return;
    try {
      await sellGear(defId);
      await refreshBalance();
      // Re-reads the catalogue and what this delver is wearing together, then
      // repaints both the doll and this shelf.
      await refreshGear();
    } catch (err) {
      showError(gearErrEl, errorMessage(err));
    }
  }

  /** Sell a skin or emote back. Pays Ember, because Ember is what it cost. */
  async function onSellCosmetic(cosmeticDefId: string): Promise<void> {
    hideError(cosmeticsErrEl);
    const def = cosmeticCatalog.find((d) => d.id === cosmeticDefId);
    const owned = myCosmetics.find((c) => c.cosmeticDefId === cosmeticDefId);
    if (!def || owned?.buybackEmber === undefined) return;
    if (!confirmSell(cosmeticLabel(def.id, def.name), owned.buybackEmber, 'ember')) return;
    try {
      await sellCosmetic(cosmeticDefId);
      await Promise.all([refreshBalance(), refreshCosmetics()]);
    } catch (err) {
      showError(cosmeticsErrEl, errorMessage(err));
    }
  }

  /** The Shop's Gear shelf — the ladder's bottom rung, and the only rung for sale. */
  function renderGearShelf(): void {
    const wax = balance?.soft ?? 0;
    const card = (row: GearCatalogRow): string => gearShelfCardMarkup({
      id: row.id,
      name: row.name,
      // The server sends the enum ('HEAD'); the card shows the slot's name.
      slot: Object.hasOwn(GEAR_SLOT_LABEL, row.slot) ? tc('gear slot', GEAR_SLOT_LABEL[row.slot as GearSlot]) : row.slot,
      line: row.line === 'WARD' ? t('Ward') : t('Weave'),
      weightKg: row.weightKg,
      priceWax: row.priceWax,
      buybackWax: row.buybackWax,
      tierName: row.tierName,
      owned: row.owned,
      currency: row.currency,
      // Worn by the delver the hub is currently showing, so the card can say
      // "Worn" instead of offering to equip what is already on.
      equipped: Object.values(wornGear?.worn ?? {}).some((piece) => piece?.id === row.id),
      statLine: statLines(row.stats).join(' · '),
    }, (row.currency === 'PREMIUM' ? (balance?.premium ?? 0) : wax) >= (row.priceWax ?? 0));

    // The whole ladder, a rung at a time (2026-09-06). User: "add tier 2 and 3
    // gear to the shop."
    //
    // Grouped by tier rather than listed flat, because the price steps 5x and
    // then 3x again between them: forty-two cards in one grid would read as
    // one shelf with wild prices on it instead of as three floors of a
    // dungeon, which is what it is. The heading names the floor the pieces
    // drop on, so the shelf and the run describe the same place.
    //
    // The old second half — "Found — no shelf sells these" — is gone with
    // this. It existed because a found tier-2/3 piece appeared nowhere in the
    // hub except its own paperdoll slot, so a duplicate had no home. Every
    // piece now has a card on its own tier's shelf, owned or not.
    const rungs: { tier: 1 | 2 | 3; heading: string }[] = [
      { tier: 1, heading: t('Cellars \u2014 the ladder\u2019s first rung') },
      { tier: 2, heading: t('Ossuary \u2014 found on the second floor, or bought here') },
      { tier: 3, heading: t('Vaults \u2014 the deepest gear in the game') },
    ];
    // The Chandlery gets its own section rather than sitting inside the rung
    // it copies (2026-09-07). It landed under "Vaults — found on the third
    // floor" on the first pass, which is a sentence that is simply not true of
    // it: nothing in the dungeon drops one. A heading that lies about where a
    // piece comes from is worse than no heading.
    const chandlery = gearCatalog.filter((row) => row.variantOf !== undefined);
    const laddered = (tier: 1 | 2 | 3): GearCatalogRow[] =>
      gearCatalog.filter((row) => row.tier === tier && row.variantOf === undefined);
    gearListEl.innerHTML = rungs.map(({ tier, heading }) => {
      const rows = laddered(tier);
      if (rows.length === 0) return '';
      return shelfHeadingMarkup(heading) + gridMarkup(rows.map(card), t('Nothing on this rung yet.'));
    }).join('')
      + (chandlery.length === 0 ? '' : shelfHeadingMarkup(
        t('The Chandlery \u2014 made, not found. Every piece the numbers of a Vaults one'))
        + gridMarkup(chandlery.map(card), ''));

    gearListEl.querySelectorAll<HTMLButtonElement>('button[data-buy-gear]').forEach((btn) => {
      btn.addEventListener('click', () => onBuyGearFromShop(btn.dataset.buyGear!));
    });
    // Equip from the shelf — the other half of "buy and equip in the shop".
    gearListEl.querySelectorAll<HTMLButtonElement>('button[data-equip-gear-shop]').forEach((btn) => {
      btn.addEventListener('click', () => onEquipGearFromShop(btn.dataset.equipGearShop!));
    });
    gearListEl.querySelectorAll<HTMLButtonElement>('button[data-sell-gear]').forEach((btn) => {
      btn.addEventListener('click', () => onSellGear(btn.dataset.sellGear!));
    });
  }

  /**
   * Buying from the shelf, rather than from the picker.
   *
   * Does not equip afterwards, unlike `onPurchaseGear` in the inventory: there
   * is no open slot to put it in from here, and silently changing what a
   * delver is wearing from a shop tab would be a change the player never saw.
   */
  /**
   * Equip from the Shop's own shelf (2026-09-06).
   *
   * The inventory picker is the wardrobe now and no longer sells, so the shelf
   * has to be able to put a piece on: user, *"All other in shop, you can buy
   * and equip."* It reads the slot off the def rather than being told one,
   * because a shelf card knows what it is and there is no open slot to infer.
   */
  async function onEquipGearFromShop(defId: string): Promise<void> {
    const row = gearCatalog.find((r) => r.id === defId);
    if (!row || !selectedCharacterId) return;
    hideError(gearErrEl);
    const characterId = selectedCharacterId;
    try {
      const next = await equipGear(characterId, row.slot, defId);
      if (selectedCharacterId !== characterId) return;
      wornGear = next;
      gearCharacterId = characterId;
      renderHeroPlate();
      await refreshLoadout();
      renderGearShelf();
      renderInventory();
    } catch (err) {
      showError(gearErrEl, errorMessage(err));
    }
  }

  async function onBuyGearFromShop(defId: string): Promise<void> {
    hideError(gearErrEl);
    try {
      await purchaseGear(defId);
      gearCatalog = await getGearCatalog();
      await refreshBalance();
      renderGearShelf();
      renderInventory();
    } catch (err) {
      showError(gearErrEl, errorMessage(err));
    }
  }

  async function refreshCosmetics(): Promise<void> {
    try {
      const [catalog, mine] = await Promise.all([getCosmeticCatalog(), getMyCosmetics()]);
      cosmeticCatalog = catalog;
      myCosmetics = mine;
      renderCosmetics();
      // The stage previews the equipped skin, so equipping or unequipping one
      // has to repaint it — otherwise the shop claims a change the lobby does
      // not show, which is the worst possible advert for a cosmetics store.
      renderHeroPlate();
    } catch (err) {
      showError(cosmeticsErrEl, errorMessage(err));
    }
  }

  async function onPurchaseCosmetic(cosmeticDefId: string): Promise<void> {
    hideError(cosmeticsErrEl);
    try {
      await purchaseCosmetic(cosmeticDefId);
      await Promise.all([refreshBalance(), refreshCosmetics()]);
    } catch (err) {
      showError(cosmeticsErrEl, errorMessage(err));
    }
  }

  /**
   * Wear a skin, or take one off — from the Inventory (2026-09-07).
   *
   * User: *"Skins and emojis should be equiped here. Not in shop for sure."*
   *
   * One handler for both directions, because from the picker's point of view
   * they are one action: an empty id is the Default row, which is choosing to
   * wear nothing rather than a separate Unequip control. That is the same call
   * the gear picker makes about its own list.
   *
   * The picker closes on a pick. It is a decision with one outcome, and a list
   * that stays open after you have chosen invites a second choice nobody came
   * for — the same reason the arms picker closes.
   *
   * Errors land on the Inventory's own line, not the Shop's: this is where the
   * player is standing, and a failure reported two panels away is a failure
   * nobody sees.
   */
  async function onPickSkin(cosmeticDefId: string): Promise<void> {
    const character = characters.find((c) => c.id === selectedCharacterId);
    if (!character) return;
    hideError(inventoryErrEl);
    skinPickerOpen = false;
    try {
      if (cosmeticDefId) await equipCosmetic(cosmeticDefId, character.id);
      else await unequipCosmetic(character.id);
      await refreshCosmetics();
      // The figure above the picker wears what was just chosen, so the stage
      // has to be told as well — `renderCharacters` is what re-reads the
      // equipped skin for the hub's own 3D stage.
      renderCharacters();
    } catch (err) {
      showError(inventoryErrEl, errorMessage(err));
    }
    renderInventory();
  }

  async function refreshQuests(): Promise<void> {
    try {
      myQuests = await getActiveQuests();
      renderQuests();
      announceNewCompletions();
    } catch (err) {
      showError(questsErrEl, errorMessage(err));
    }
  }

  /** Tell the player about anything finished-but-unannounced, and hand them the reward on the spot. */
  function announceNewCompletions(): void {
    const announced = readAnnounced();
    const completed = myQuests.filter((uq) => uq.status === 'COMPLETED');
    for (const uq of completed) {
      if (announced.has(uq.id)) continue;
      announced.add(uq.id);
      toasts.show({
        heading: t('Quest complete'),
        title: t(uq.quest.name),
        detailHtml: rewardLineMarkup({
          name: t(uq.quest.name),
          rewardXp: uq.quest.rewardXp,
          rewardSoft: uq.quest.rewardSoft,
          rewardPremium: uq.quest.rewardPremium,
        }) ?? undefined,
        // The reward, one tap away, rather than "go and find the panel". The
        // toast dismisses only once the claim actually succeeds — see
        // `hub-toast.ts` on why this is a button and not automatic.
        action: { label: t('Claim'), run: () => onClaimQuest(uq.id) },
      });
    }
    // Prune to what is still outstanding: a claimed quest leaves this list
    // entirely, and its id never needs remembering again.
    const outstanding = new Set(completed.map((uq) => uq.id));
    writeAnnounced(new Set([...announced].filter((id) => outstanding.has(id))));
  }

  /**
   * Claim a completed quest's reward.
   *
   * Rethrows, unlike its siblings here: the toast's Claim button needs to know
   * whether the grant actually happened, so it can stay on screen and say why
   * rather than dismissing itself over a failed request and leaving the player
   * believing they were paid. The panel's own button ignores the rejection —
   * the error is already on screen next to it.
   */
  async function onClaimQuest(userQuestId: string): Promise<void> {
    hideError(questsErrEl);
    try {
      await claimQuestReward(userQuestId);
      await Promise.all([refreshQuests(), refreshBalance()]);
    } catch (err) {
      showError(questsErrEl, errorMessage(err));
      throw err;
    }
  }

  /**
   * The Wax shelf.
   *
   * Two independent reasons a bundle can be unbuyable, drawn differently:
   * not enough Ember is the ordinary unaffordable price button, while the
   * daily cap gets its own words — waiting is the fix for one and buying more
   * Ember is the fix for the other, and one grey button cannot say both.
   */
  function renderWaxShop(): void {
    if (!waxShelf) return;
    const ember = balance?.premium ?? 0;
    const cards = waxShelf.bundles.map((bundle) => waxBundleCardMarkup(bundle, {
      affordable: ember >= bundle.priceEmber,
      blocked: bundle.priceEmber > waxShelf!.remainingToday,
    }));
    waxBodyEl.innerHTML = gridMarkup(cards, t('No bundles available.')) + waxCapLineMarkup(waxShelf);
    waxBodyEl.querySelectorAll<HTMLButtonElement>('button[data-buy-wax]').forEach((btn) => {
      btn.addEventListener('click', () => onPurchaseWax(btn.dataset.buyWax!));
    });
  }

  async function onPurchaseWax(bundleId: string): Promise<void> {
    hideError(waxErrEl);
    try {
      await purchaseWaxBundle(bundleId);
      // Both change: the wallet, and how much of today's allowance is left.
      await Promise.all([refreshBalance(), refreshWaxBundles()]);
    } catch (err) {
      showError(waxErrEl, waxErrorMessage(err));
    }
  }

  /** The one refusal this endpoint has that is not about money. */
  function waxErrorMessage(err: unknown): string {
    const raw = errorMessage(err);
    if (raw.includes('daily_exchange_cap_exceeded')) {
      return t('That would pass today’s limit on buying Wax. It rolls off through the day.');
    }
    return raw;
  }

  async function refreshWaxBundles(): Promise<void> {
    try {
      waxShelf = await getWaxBundles();
      renderWaxShop();
    } catch (err) {
      showError(waxErrEl, errorMessage(err));
    }
  }

  function onQueueClick(): void {
    hideError(queueErrEl);
    if (queueState === 'idle') {
      if (!partyReadyToQueue() || !selectedCharacterId) return; // button is disabled in this state, but guard anyway
      queueState = 'queued';
      startQueueSearchTimer();
      renderQueueUi();
      joinQueue(squadMode, selectedCharacterId);
    } else if (queueState === 'queued') {
      leaveQueue();
    }
  }

  function onQueued(_data: { position: number }): void {
    queueState = 'queued';
    startQueueSearchTimer(); // no-op if already ticking — position doesn't reset "how long have I waited"
    renderQueueUi();
  }
  function onQueueLeft(): void {
    queueState = 'idle';
    stopQueueSearchTimer();
    queueStatusEl.textContent = '';
    renderQueueUi();
  }
  function onQueueFoundEvent(data: MatchFoundPayload): void {
    // A tab that never queued does not take the seat (2026-10-16). Another tab
    // of the same account consuming it is how "queued the Arena, landed in a
    // Delve" happened; the server now aims Found at the tab that pressed Play,
    // and this is the client half of that.
    if (queueState === 'idle') return;
    queueState = 'found';
    stopQueueSearchTimer();
    // The server's word for which game this is, not this tab's picker.
    const arena = isArenaMode(data.mode ?? squadMode);
    queueStatusEl.textContent = arena ? t('Match found — entering the arena…') : t('Match found — entering the dungeon…');
    renderQueueUi();
    onQueueFound(data.reservation);
  }
  function onHubMemberJoined(data: { userId: string; username: string }): void {
    presentUsers.set(data.userId, data.username);
    renderPresence();
  }
  function onHubMemberLeft(data: { userId: string }): void {
    presentUsers.delete(data.userId);
    renderPresence();
  }
  function onServerError(err: { code: string; message: string }): void {
    showError(queueErrEl, serverText(err.message));
    // design/gdd/energy.md — a refused queue means the balance moved under us
    // (another tab, another device, a refill that came due). Re-read it so the
    // flow shows the real number instead of the one it refused against.
    if (err.code === 'NO_ENERGY') void refreshEnergy();
  }

  function onPartyInviteReceived(data: PartyInviteReceivedPayload): void {
    // Core Rule #1a/PartyService — only one pending invite per invitee at a
    // time server-side; a second arriving would mean the first was already
    // resolved (accept/decline/disconnect), so it's safe to just replace it.
    incomingInvite = data;
    renderIncomingInviteBanner();
  }
  function onPartyMemberJoined(data: PartyMemberJoinedPayload): void {
    // Full snapshot (partyId/mode/leaderId/memberIds) — replaces local state
    // wholesale rather than patching it, so this works identically whether
    // the local player is the one who just joined or an existing member.
    myParty = { id: data.partyId, mode: data.mode, leaderId: data.leaderId, memberIds: data.memberIds };
    squadMode = data.mode;
    // The party's wire mode is authoritative — a member who was invited into
    // a Duo did not pick it here, so the picker follows rather than leads.
    adoptSquadMode(data.mode);
    renderParty();
    renderPresence();
    renderPlayPanel();
    renderQueueUi();
  }
  function onPartyMemberLeft(data: PartyMemberLeftPayload): void {
    if (!myParty || myParty.id !== data.partyId) return;
    myParty.memberIds = myParty.memberIds.filter((id) => id !== data.userId);
    renderParty();
    renderPresence();
    renderQueueUi();
  }
  function onPartyDisbanded(data: { partyId: string }): void {
    if (myParty?.id === data.partyId) myParty = null;
    renderParty();
    renderPresence();
    renderQueueUi();
  }
  function onPartyQueueCancelled(): void {
    queueState = 'idle';
    stopQueueSearchTimer();
    queueStatusEl.textContent = t('A party member disconnected — removed from the queue.');
    renderQueueUi();
  }

  function onReadyCheck(data: ReadyCheckPayload): void {
    // Same rule as Found: a tab that is not queueing does not answer for one that is.
    if (queueState === 'idle') return;
    activeReadyCheck = data;
    // Anchored on this machine's clock at receipt rather than on the server's
    // `deadline` timestamp - see ReadyCheckPayload.timeoutMs. Falls back to
    // the old arithmetic if the field is missing, which is no worse than what
    // it replaces.
    readyCheckExpiresAt = readyCheckExpiryAt(data, Date.now());
    queueState = 'ready_check';
    // Stop overwriting queueStatusEl/the searching overlay (the ready-check
    // overlay owns the screen now) but keep queueSearchStartedAt ticking
    // underneath — a decline/timeout drops back into the same real wait,
    // not a fresh one.
    if (queueSearchTimer) { clearInterval(queueSearchTimer); queueSearchTimer = null; }
    searchingOverlayEl.hidden = true;
    renderReadyCheckOverlay();
    renderQueueUi();
  }
  function onReadyCheckFailed(data: ReadyCheckFailedPayload): void {
    activeReadyCheck = null;
    // matchmaking.md Core Rule #6a — the server already re-queued this
    // entry at the front; no new join_queue call needed from the client.
    queueState = 'queued';
    queueStatusEl.textContent = data.reason === 'timeout'
      ? t('Ready check timed out — back in queue.')
      : t('Match declined — back in queue.');
    startQueueSearchTimer(false); // leave the message above visible for the first tick
    renderReadyCheckOverlay();
    renderQueueUi();
  }
  function onReadyAcceptClick(): void {
    if (!activeReadyCheck) return;
    acceptReadyCheck(activeReadyCheck.checkId);
    // Stay in 'ready_check' until matchmaking.found (this entry accepted,
    // but the check as a whole isn't resolved until everyone has).
  }
  function onReadyDeclineClick(): void {
    if (!activeReadyCheck) return;
    declineReadyCheck(activeReadyCheck.checkId);
    // The server requeues a decline at the front of the queue automatically
    // (same as a timeout, matchmaking.md Core Rule #6a) — this client just
    // waits for the matching `matchmaking.ready_check_failed` to confirm it.
  }

  socket.on(MATCHMAKING_EVENTS.QUEUED, onQueued);
  socket.on(MATCHMAKING_EVENTS.LEFT, onQueueLeft);
  socket.on(MATCHMAKING_EVENTS.FOUND, onQueueFoundEvent);
  socket.on(HUB_EVENTS.MEMBER_JOINED, onHubMemberJoined);
  socket.on(HUB_EVENTS.MEMBER_LEFT, onHubMemberLeft);
  socket.on(PARTY_EVENTS.INVITE_RECEIVED, onPartyInviteReceived);
  socket.on(PARTY_EVENTS.MEMBER_JOINED, onPartyMemberJoined);
  socket.on(PARTY_EVENTS.MEMBER_LEFT, onPartyMemberLeft);
  socket.on(PARTY_EVENTS.DISBANDED, onPartyDisbanded);
  socket.on(PARTY_EVENTS.QUEUE_CANCELLED, onPartyQueueCancelled);
  socket.on(MATCHMAKING_EVENTS.READY_CHECK, onReadyCheck);
  socket.on(MATCHMAKING_EVENTS.READY_CHECK_FAILED, onReadyCheckFailed);
  socket.on('server:error', onServerError);

  $('hub-logout').addEventListener('click', onLogout);

  /*
   * The hub is the one screen a player sits in with nothing to listen *for*
   * (see MUSIC_TRACKS' note on why the dungeon has no bed). Start it on mount
   * and let the toggle own the on/off state from there.
   *
   * Not awaited: the bed is a ~1MB fetch plus a decode, and no part of the hub
   * should wait on it. A muted player never triggers that fetch at all.
   */
  const audioEngine = new AudioEngine();
  audioEngine.ensureStarted();
  void audioEngine.playMusic(MUSIC_TRACKS.HUB);
  const unbindAudioSettings = bindAudioToSettings(audioEngine);
  // Through the store, not straight into the engine: the store is what the
  // engine follows (bindAudioToSettings), and a header click that bypassed it
  // would be undone by the next slider move re-emitting the old on/off.
  const musicToggle = new MusicToggle($('hub-music-toggle'), (enabled) => {
    settingsStore.patchAudio({ musicEnabled: enabled });
  });
  // The header button and the Audio section move the same preference, so the
  // toggle repaints from the store rather than only from its own click.
  const unbindMusicToggle = settingsStore.subscribe(({ audio }) => musicToggle.setEnabled(audio.musicEnabled));
  $<HTMLButtonElement>('hub-settings').addEventListener('click', () => openSettings());
  // Before the click listener below, on purpose: while a run can start, the
  // press belongs to the hold (see ui/hold-to-play.ts) and the click is not
  // passed on; otherwise the click below runs exactly as it always has.
  const disposeHold = bindHoldToPlay(queueBtn, {
    holdMs: 650,
    isHoldable: () => queueState === 'idle' && launchReady,
    onConfirm: () => {
      hideError(playErrEl);
      setPanel(null);
      void onQueueClick();
    },
  });
  runOptionsBtn.addEventListener('click', () => { setPanel('play'); renderPlayPanel(); });
  queueBtn.addEventListener('click', () => {
    // Idle opens the flow; anything else is the leave-queue path the button
    // has always been.
    if (queueState === 'idle') { setPanel('play'); renderPlayPanel(); return; }
    void onQueueClick();
  });
  playConfirmBtn.addEventListener('click', () => {
    hideError(playErrEl);
    setPanel(null);
    void onQueueClick();
  });
  searchingCancelBtn.addEventListener('click', onQueueClick); // same leaveQueue path as the inline "Leave Queue" button
  partyLeaveBtn.addEventListener('click', () => leaveParty());
  readyAcceptBtn.addEventListener('click', onReadyAcceptClick);
  readyDeclineBtn.addEventListener('click', onReadyDeclineClick);
  insuranceExpandBtn.addEventListener('click', onExpandInsurance);

  // Deferred to a microtask so the initial render below has run first — the
  // click path reads the selected character and mode straight out of the DOM
  // it is about to populate.
  // Opens the flow directly rather than through a synthetic click, which the
  // hold binding would now read as a keyboard confirm and queue at once.
  if (autoQueue) queueMicrotask(() => { if (queueState === 'idle') { setPanel('play'); renderPlayPanel(); } });

  /**
   * The two the tutorial's opening question depends on: whether this account
   * has a delver yet, and whether it has cover. Started with the rest of the
   * boot fan-out, but the tour waits on them — asking a player to make their
   * first delver when the roster simply had not loaded yet is a tutorial
   * teaching a falsehood.
   */
  const tutorialPrerequisites = Promise.allSettled([refreshCharacters(), refreshInsurance()]);
  renderPlayPanel();
  renderParty();
  renderQueueUi();
  refreshBalance();
  refreshStash();
  refreshContracts();
  refreshCosmetics();
  refreshQuests();
  refreshConsumables();
  refreshRites();
  refreshEnergy();
  refreshEnergyBundles();
  refreshEmberOffers();
  refreshWaxBundles();
  refreshCharacterSlots();
  // Read before `handleCheckoutReturn` strips it: a player just back from
  // paying is not greeted with another offer.
  const _params = new URLSearchParams(window.location.search);
  const returningFromCheckout = _params.has('purchase') || _params.has('order');
  handleCheckoutReturn();


  refreshGameModes();
  refreshEquipmentCatalog();
  refreshCommissions();
  renderShopTabs();

  /**
   * Starts the hub tour, unless this account has already been through it or
   * turned it down. Failing to reach the endpoint means no tutorial rather
   * than a tutorial that cannot record anything: a tour that restarts from
   * step one on every reload is worse than none.
   */
  function startTutorial(): Promise<boolean> {
    return getTutorialState().then((state) => {
      if (state.skipped || state.completedAt) return false;
      tutorial?.dispose();
      tutorial = new TutorialRunner({
        chapter: 'hub',
        container: root,
        seen: state.seen,
        skipped: false,
        context: {
          characterCount: characters.length,
          insuranceCharges: insurance?.charges ?? 0,
          hasKeyboard: !!window.matchMedia?.('(pointer: fine)').matches,
        },
        persistence: {
          markSeen: markTutorialStepSeen,
          skip: skipTutorial,
          complete: completeTutorial,
        },
      });
      tutorial.start();
      return true;
    }).catch(() => false);
  }

  /**
   * The lobby Ember offer (2026-10-09) — once a day, and only into a quiet
   * hub: never over the tour (a first-time player is learning the room, not
   * shopping), never on top of a "Queue Again" search, never straight after a
   * checkout. The short delay lets the entry veil clear first, so the offer
   * arrives into the room rather than with it.
   */
  async function maybeShowOffer(): Promise<void> {
    if (autoQueue || returningFromCheckout) return;
    const storage = (() => { try { return window.localStorage; } catch { return null; } })();
    if (!shouldShowOffer(user.id, new Date(), storage)) return;
    const { offer } = await getEmberLobbyOffer().catch(() => ({ offer: null }));
    if (!offer) return;
    await new Promise((r) => setTimeout(r, 700));
    if (unmounted || queueState !== 'idle') return;
    markOfferShown(user.id, new Date(), storage);
    offerPopup = openEmberOfferPopup(root, offer, {
      onClaim: async (o) => {
        try {
          const deposit = await createEmberDeposit({ productId: `offer:${o.id}`, method: selectedMethod, currency: selectedCurrency });
          window.location.assign(deposit.redirectUrl);
          return null;
        } catch (err) {
          return emberErrorMessage(err);
        }
      },
    });
  }

  void tutorialPrerequisites.then(startTutorial).then((touring) => { if (!touring) void maybeShowOffer(); });

  /**
   * Settings' "Replay the tutorial" fires this rather than reaching into the
   * hub, so the same control works from the in-run settings modal too — there
   * it resets the record and this listener picks it up on the next hub mount.
   */
  const onReplayTutorial = (): void => { void startTutorial(); };
  window.addEventListener('tallow:tutorial-replay', onReplayTutorial);

  return () => {
    disposeHold();
    hubDepth.dispose();
    hubAmbience?.dispose();
    window.removeEventListener('tallow:tutorial-replay', onReplayTutorial);
    unmounted = true;
    offerPopup?.close();
    methodPickerHandle?.destroy();
    currencySelectorHandle?.destroy();
    tutorial?.dispose();
    socket.off(MATCHMAKING_EVENTS.QUEUED, onQueued);
    socket.off(MATCHMAKING_EVENTS.LEFT, onQueueLeft);
    socket.off(MATCHMAKING_EVENTS.FOUND, onQueueFoundEvent);
    socket.off(HUB_EVENTS.MEMBER_JOINED, onHubMemberJoined);
    socket.off(HUB_EVENTS.MEMBER_LEFT, onHubMemberLeft);
    socket.off(PARTY_EVENTS.INVITE_RECEIVED, onPartyInviteReceived);
    socket.off(PARTY_EVENTS.MEMBER_JOINED, onPartyMemberJoined);
    socket.off(PARTY_EVENTS.MEMBER_LEFT, onPartyMemberLeft);
    socket.off(PARTY_EVENTS.DISBANDED, onPartyDisbanded);
    socket.off(PARTY_EVENTS.QUEUE_CANCELLED, onPartyQueueCancelled);
    socket.off(MATCHMAKING_EVENTS.READY_CHECK, onReadyCheck);
    socket.off(MATCHMAKING_EVENTS.READY_CHECK_FAILED, onReadyCheckFailed);
    socket.off('server:error', onServerError);
    if (readyCheckCountdownTimer) clearInterval(readyCheckCountdownTimer);
    stopQueueSearchTimer();
    document.removeEventListener('keydown', onPanelKeydown);
    stage.dispose();
    toasts.dispose();
    unbindMusicToggle();
    musicToggle.dispose();
    unbindAudioSettings();
    // A hub that unmounts must not leave a settings overlay on top of whatever
    // replaces it — signing out from inside the modal is the obvious way in.
    closeSettings();
    // Fade the bed out rather than letting root.innerHTML = '' cut it: the node
    // graph lives on the AudioContext, not in the DOM, so clearing the markup
    // would otherwise leave the hub's music playing over the next screen. The
    // dispose() that follows closes the context, so the fade is only audible
    // once it has had its OUT_S to run.
    void audioEngine.stopMusic().then(() => audioEngine.dispose());
    root.innerHTML = '';
  };
}

/**
 * `knight` → `Knight`, `TANK` → `Tank`. Data that was only ever seen through
 * Cinzel — a capitals-only face — was stored in whatever case the API sends;
 * Vollkorn shows it exactly as written (2026-10-09).
 */
function titleCase(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;
}

/** Roles as shown. Marked here, translated where drawn. */
const ROLE_NAME: Record<string, string> = { TANK: N_('Tank'), HEALER: N_('Healer'), DPS: N_('DPS') };

function roleName(role: string): string {
  const key = ROLE_NAME[role];
  return key ? t(key) : titleCase(role);
}

/** `Knight · Level 7`, in the active language. */
function classLevelLine(characterClass: string, level: number): string {
  return t('{class} · Level {n}', { class: titleCase(classDisplayName(characterClass)), n: level });
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}
